/**
 * CambrianStage — Phase 3, "Cambrian Survivors".
 *
 * A Vampire-Survivors-style roguelite layered onto Evolve's existing
 * architecture. The player drives a small creature in a 4000×4000
 * Cambrian-sea biome, autoattacks fire from body-mounted mutations
 * (front / rear / flanks / dorsal / ventral), and a living 4-tier
 * food chain spawns + hunts in the background.
 *
 * Design notes:
 *  - Camera follows the player with a soft deadzone, like MulticellStage.
 *  - Movement is mouse-follow with momentum (VS-style — input is steering,
 *    not snap-to-cursor). Heading drives mutation attack directions.
 *  - All ecosystem work is delegated to EcosystemSystem. The stage owns
 *    only the per-creature ECS entities + drawing.
 *
 * Milestone 1 deliverable: skeleton + camera + player + HUD shell. The
 * full ecosystem + body-zone + level-up + apex layers ship in M2-M5.
 */

import { Container, Graphics, Sprite, Assets, Texture, Text } from 'pixi.js';
import type { Stage, StageContext } from '@/core/StageManager';
import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';
import { player } from '@/systems/PlayerState';
import { Position } from '@/components';
import { EcosystemSystem, type EcosystemConfig } from '@/systems/EcosystemSystem';
import { ParticleSystem } from '@/systems/ParticleSystem';
import { VERSION } from '@/version';
import type { CreatureDef, CreatureTier } from './types';
import { CreatureComp, ProjectileComp } from './types';
import { LevelUpModal } from './LevelUpModal';
import { EvolutionIntro, type IntroCellType } from './EvolutionIntro';
import { BodyPlanOverlay } from './BodyPlanOverlay';
import { CambrianTutorial } from './CambrianTutorial';
import { LevelCompleteModal } from './LevelCompleteModal';
import { CambrianCodex } from './CambrianCodex';
import { AchievementSystem } from '@/systems/AchievementSystem';
import { createTouchPad, IS_TOUCH, type TouchPad } from '@/ui/TouchControls';
import type { CodexEntry } from '@/data/types';
import { CodexSpotlight, type SpotlightTarget } from '@/ui/CodexSpotlight';
import { MAX_PART_LEVEL, PART_STATS, partLevel, rollOffers, type PartId, type PartLevels } from './parts';
import { stageManager } from '@/core/StageManager';
import { showStartMenu } from '@/ui/StartMenu';
import { CausticSeaMesh } from '@/shaders/causticSea';
import { AdvancedBloomFilter } from 'pixi-filters/advanced-bloom';
import {
  drawPlayerHalo, drawPlayerComposite,
  drawOpabinia, drawMarrella, drawWiwaxia, drawHallucigenia, drawThreatRing,
  drawCopepod, drawDiatom, drawOstracod, drawGlowMedusa, drawToxicSiphon,
  drawPlanktonOrganism, drawTrilobite, drawHuntingWorm, drawAnomalocaris,
  type PlayerDrawCtx, type PartFx, type CreatureDrawCtx,
} from './draw';
import '@/ui/ui.css';

// --- world + tuning ---

// Values are sourced from /src/data/cambrian/stageConfig.json at init.
// These DEFAULT_* values are fallbacks if the load fails, so the stage
// can still boot in a degraded state for debugging.
const DEFAULT_WORLD_W = 4000;
const DEFAULT_WORLD_H = 4000;
const PLAYER_BOUND_PAD = 60;
const CAM_DEADZONE_FRAC_X = 0.4;
const CAM_DEADZONE_FRAC_Y = 0.4;

const PLAYER_BASE_RADIUS = 22;
const PLAYER_BASE_SPEED = 240;
const PLAYER_BASE_HP = 100;

// --- Lunge: the player's manual bite. Left-click / Space bursts toward the
// cursor; anything the body passes through during the burst gets bitten.
// Costs ATP (same currency as L1/L2's dash) so it's a decision, not spam.
/** Glow medusa heals this fraction of max HP; toxic siphonophore costs this much. */
const HEAL_FRAC = 0.2;
const POISON_FRAC = 0.3;
const LUNGE_SPEED = 820;          // world units/sec during the burst
const LUNGE_DURATION = 0.26;      // seconds of burst
const LUNGE_COOLDOWN = 0.3;       // seconds before the next lunge — bites chain (frenzy shortens it)
const FLICK_SPEED = 760;
const FLICK_DURATION = 0.2;
const FLICK_COOLDOWN = 0.3;
/** Two right-clicks within this many ms = dash left instead of right. */
const FLICK_DOUBLE_MS = 220;
// ATP pays for lunges and dodges: a full bar is two bites or two dodges
// back to back. It only refills by itself — fast, after a short beat.
const FLICK_ATP_COST = 50;
const FLICK_IFRAMES = 0.5;
const SILT_RADIUS = 95;
const SILT_LIFE = 2.6;
const ATP_MAX = 100;
const ATP_REGEN = 34;             // per second — empty to full in ~3s
const ATP_REGEN_DELAY = 0.35;     // pause after a dodge before refilling
/** Each lunge (bite) costs this much ATP — a full bar chains two bites. */
const LUNGE_ATP_COST = 45;
const BITE_BASE_DAMAGE = 26;
/** Bite landed from behind the prey (along its heading) = ambush crit. */
const AMBUSH_DOT = 0.45;
const AMBUSH_MULT = 2.0;

// --- Frenzy: L1's combo, reborn as a kill chain. Every player kill within
// the window stacks; stacks multiply biomass and speed up lunges. Taking
// damage breaks it.
const FRENZY_WINDOW = 3.2;        // seconds to land the next kill
const FRENZY_BIOMASS_PER_STACK = 0.15;
const FRENZY_BIOMASS_CAP = 3.0;
const FRENZY_SPEED_PER_STACK = 0.02;
const FRENZY_SPEED_CAP = 0.30;
const FRENZY_COOLDOWN_PER_STACK = 0.07;
const FRENZY_COOLDOWN_FLOOR = 0.4; // lunge cooldown never drops below 40%
const FRENZY_MILESTONE_EVERY = 5;

// --- Growth: the L1/L2 loop. Biomass is the goal; crossing a threshold
// grows the body, opens a body-zone slot (mutation pick) and, at set
// points, flips a whole creature tier from threat to prey.
const STAGE_GOAL_BIOMASS = 6_000_000;
// First growth comes fast (a couple of minutes of plankton), then each
// step is longer than the last: 20K, +40K, +90K, +170K, +280K, +500K,
// +800K, +1.2M, +1.5M.
const GROWTH_THRESHOLDS = [
  20_000, 60_000, 150_000, 320_000, 600_000,
  1_100_000, 1_900_000, 3_100_000, 4_600_000,
];
/** Body radius grows this much per threshold crossed. */
const GROWTH_SIZE_PER_STEP = 0.07;
/** Thresholds crossed → highest creature tier the player can bite through. */
function maxBiteTierFor(growthIdx: number): CreatureTier {
  if (growthIdx >= 8) return 4;
  if (growthIdx >= 5) return 3;
  if (growthIdx >= 2) return 2;
  return 1;
}
/** Creatures tier 2+ spawn this much bigger/tougher per evolution. */
const DANGER_PER_GROWTH = 0.1;
/** Camera zoom range. Touch screens start at ZOOM_MIN — the widest view —
 *  and the player pinches in from there; desktop starts at 1. */
const ZOOM_MIN = 0.35;
const ZOOM_MAX = 1.6;
function startZoom(): number {
  return IS_TOUCH ? ZOOM_MIN : 1;
}
/** Seconds of play between two codex first-encounter cards, and the
 *  breather after the tutorial before the first one. */
const CODEX_GAP = 45;
const CODEX_AFTER_TUTORIAL = 12;
/** The apex predator enters the sea once you've evolved this many times. */
const APEX_GROWTH = 5;
/** Biting something above your size only glances. */
const GLANCE_DAMAGE_FRAC = 0.15;

/** Seconds a hunter telegraphs before its strike burst, and how long the burst lasts. */
const HUNT_WINDUP = 0.35;
const HUNT_BURST = 0.45;

// --- Quill launcher: volleys at random enemies within range.
const QUILL_RANGE = 480;
const QUILL_SPEED = 560;
/** Venom from the stinger ticks for this long. */
const VENOM_SEC = 3;

// --- Plankton lines: tier-1 food spawns in straight lines that hold for a
// few seconds, then drift apart. Eating a line in order chains a bonus.
const LINE_LEN = 6;
const LINE_SPACING = 34;
const LINE_HOLD_SEC = 7;
const LINE_COMBO_WINDOW = 3.0;
const LINE_BONUS_PER_STEP = 0.35;

/** Live run snapshot under run.stageData.cambrian.live (see saveLive). */
interface CambrianLive {
  parts?: PartLevels;
  evolutionsEarned?: number;
  deathStreak?: number;
  hp: number;
  atp: number;
  apexSpawned: boolean;
  /** Level-complete screen already shown for this run. */
  completed?: boolean;
  killsByTier?: Record<number, number>;
  deaths?: number;
}
/** What MulticellStage.onGoalReached leaves under meta.prestige.carryover. */
interface Phase2Carryover {
  fromStage?: string;
  totalDnaCarry?: number;
  biomassHeadStart?: number;
  cells?: { core?: number; armor?: number; motor?: number; sensor?: number };
}

// Initial body coloration — tide-pool warm teal that mutations recolor.
const PLAYER_BODY_COLOR = 0xf9c876;
const PLAYER_OUTLINE = 0x7b4a14;

// XP curve — total XP needed to reach each level. Index 0 unused (level 1 = start).
const DEFAULT_XP_CURVE: number[] = [0, 0, 10, 26, 50, 84, 130, 190, 266, 360, 475, 615, 780, 970, 1190, 1440];

// Ecosystem caps + regen rates. Overwritten by stageConfig.json on init.
const DEFAULT_ECO_CONFIG: EcosystemConfig = {
  1: { cap: 100, regenPerSec: 4.0 },
  2: { cap: 60,  regenPerSec: 1.5 },
  3: { cap: 30,  regenPerSec: 0.4 },
  4: { cap: 1,   regenPerSec: 0.0 },
};

interface StageConfig {
  duration_seconds: number;
  biome_size: { w: number; h: number };
  tier_population_caps: Record<string, number>;
  tier_regen_rates: Record<string, number>;
  apex_spawn_time_range: { min_sec: number; max_sec: number };
  level_up_xp_curve: number[];
  player: { hp_max: number; base_speed: number; base_radius: number; starting_mutation: string };
}

export class CambrianStage implements Stage {
  readonly id = 'cambrian';

  private ctx!: StageContext;
  private layer!: Container;
  private worldContainer!: Container;
  private bgLayer!: Container;
  private causticMesh!: CausticSeaMesh;
  private kelpLayer!: Container;
  private bloomFilter!: AdvancedBloomFilter;
  private godrayFilter: { time: number } | null = null;
  private kelpInstances: Array<{ g: Graphics; baseX: number; baseY: number; baseH: number; phase: number; tint: number }> = [];
  private creatureLayer!: Container;
  private projectileLayer!: Container;
  private playerLayer!: Container;
  private hudLayer!: Container;
  // Wake FX — soft expanding puffs trailing the player & big predators so
  // they look like they're parting water. One shared Graphics, redrawn each
  // frame from a pool of aging puffs.
  private wakeLayer!: Container;
  private wakeGfx!: Graphics;
  // Puff with velocity + friction. `kind` picks the renderer: 'ripple' draws
  // a thin expanding ring (water surface ripple), 'glint' draws a small
  // bright filled dot (kicked-up water droplet). Additive-blended so both
  // read as light on water instead of opaque paint.
  private wakePuffs: Array<{
    kind: 'ripple' | 'glint';
    x: number; y: number;
    vx: number; vy: number;
    age: number; life: number;
    r0: number; r1: number;
    alpha: number; color: number;
    thickness?: number; // for ripple strokes
  }> = [];
  private wakePlayerAcc = 0;
  // Sign of the previous wag-clock sample — used to detect zero-crossings
  // (the peak of each tail stroke) so we can fire tail-beat splashes in
  // rhythm with the animation.
  private wakeLastWagSign = 0;

  // Stage-wide config (filled in init from JSON).
  private cfg!: StageConfig;
  private worldW = DEFAULT_WORLD_W;
  private worldH = DEFAULT_WORLD_H;

  // Player state — VS-style controllable creature.
  private playerEntity = 0;
  private px = DEFAULT_WORLD_W / 2;
  private py = DEFAULT_WORLD_H / 2;
  private pvx = 0;
  private pvy = 0;
  private facing = 0;
  // Smoothed body rotation — eases toward `facing` via shortest-arc lerp so
  // the sprite + zone overlays bank into turns instead of snapping. Aim
  // (autoAttack) still uses raw `facing` for instant cursor tracking.
  private bodyFacing = 0;
  // 0 = straight swim (full wag), 1 = actively turning (wag suppressed) — the
  // wag clashes with banking motion and reads as jitter, so we damp it.
  private turnIntensity = 0;
  private playerGfx!: Graphics;
  private playerHaloGfx!: Graphics;
  /** Piranha silhouette underneath the procedural overlays. Tinted warm
   *  + shifted by family on body:updated. Optional: if the asset fails to
   *  load, the procedural fallback in drawPlayerComposite still renders
   *  the full anatomy on its own. */
  private playerSprite: Sprite | null = null;
  private playerSpriteBaseScale = 1;
  /** Evolutions (part picks) earned this run — deaths can shrink you
   *  below it, but regrowing to an earned size gives no new pick. */
  private evolutionsEarned = 0;
  /** Deaths since you last grew a size; each one drops you a level further. */
  private deathStreak = 0;
  /** Camera zoom — widest on touch at the start of the level; pinch
   *  changes it for the rest of the level. */
  private worldZoom = 1;
  /** Grown body parts and their levels. */
  private parts: PartLevels = {};
  /** Part animation + cooldown state (quill reload, shield charges, flashes). */
  private partFx: PartFx = { quillLoad: 1, shieldCharges: 0, stingFlash: 0, shieldFlash: 0, thornFlash: 0, lunge: 0 };
  private quillT = 0;
  private shieldRechargeT = 0;
  private playerHpMax = PLAYER_BASE_HP;
  private playerHp = PLAYER_BASE_HP;
  /** Brief invulnerability after taking damage so a stack of creatures
   *  doesn't tear through HP in a single frame. */
  private invulnT = 0;
  /** Aggregated part effects — recomputed whenever a part changes. */
  private passives = {
    damageMult: 1.0,
    moveSpeedMult: 0.0,
    hpRegen: 0.0,
    biomassMult: 1.0,
    lungeSpeedMult: 1.0,
    atpMaxMult: 1.0,
    lungeCooldownMult: 1.0,
    atpRegenMult: 1.0,
  };
  // Lunge state.
  private playerAtp = ATP_MAX;
  /** Bar size — the power tail's top level adds a third dodge. */
  private atpMax = ATP_MAX;
  private atpRegenDelayT = 0;
  private lungeT = 0;            // burst time remaining
  private lungeCooldownT = 0;
  private lungeDirX = 1;
  private lungeDirY = 0;
  /** Creatures already bitten by the current lunge (one bite per pass). */
  private lungeHitSet = new Set<number>();
  /** Bite damage — base + front-zone mutation damage. Set in recomputePassives. */
  private biteDamage = BITE_BASE_DAMAGE;
  /** Display objects to destroy at the start of the next frame. Destroying
   *  a Graphics in the same frame it was redrawn (creatures are redrawn
   *  every frame) makes Pixi throw in updateRenderable and kills the loop;
   *  removing it from its layer now and destroying it next frame is safe. */
  private reap: Container[] = [];
  private flushReap(): void {
    if (this.reap.length === 0) return;
    for (const obj of this.reap) { obj.parent?.removeChild(obj); obj.destroy({ children: true }); }
    this.reap.length = 0;
  }
  // Silt flick (right-click): evasive burst away from the cursor that
  // leaves a cloud hunters lose you in.
  private flickT = 0;
  private flickCooldownT = 0;
  private flickDirX = 1;
  private flickDirY = 0;
  private siltClouds: Array<{ g: Graphics; x: number; y: number; r: number; age: number }> = [];
  // Frenzy state.
  private frenzy = 0;
  private frenzyT = 0;
  // Plankton line state.
  private lineSeq = 0;
  /** Line being filled by incoming tier-1 spawns. */
  private currentLine: { id: number; x: number; y: number; dir: number; n: number; defId: string } | null = null;
  /** lineId → member entity ids (pruned as members die). */
  private lines = new Map<number, { ids: number[]; bornAt: number }>();
  private lineGfx!: Graphics;
  private lineComboId = 0;
  private lineComboLastIdx = -1;
  private lineComboDir = 0;
  private lineCombo = 0;
  private lineComboAt = -99;
  // Opening cinematic (fresh runs only). While set, the world is frozen.
  private intro: EvolutionIntro | null = null;
  /** Carry banner held back until the intro ends. */
  private introBanner: { title: string; subtitle: string; durationSec: number } | null = null;
  // Growth state.
  private growthIdx = 0;
  private goalReached = false;
  private get sizeScale(): number { return 1 + this.growthIdx * GROWTH_SIZE_PER_STEP; }
  private get playerRadius(): number { return PLAYER_BASE_RADIUS * this.sizeScale; }
  private get maxBiteTier(): CreatureTier { return maxBiteTierFor(this.growthIdx); }
  private dead = false;
  private elapsed = 0;
  private apexSpawnAt = 8 * 60; // seconds — picked uniformly inside the configured range at init
  private apexEntity = 0;

  // Camera (deadzone-followed).
  private camX = this.worldW / 2;
  private camY = this.worldH / 2;

  // Input.
  private mouseX = 0;
  private mouseY = 0;
  private onPointerMove?: (e: PointerEvent) => void;
  private onPointerDown?: (e: PointerEvent) => void;
  private onKeyDown?: (e: KeyboardEvent) => void;
  private onContextMenu?: (e: MouseEvent) => void;

  // Systems.
  private ecosystem!: EcosystemSystem;
  private particles!: ParticleSystem;
  private fxLayer!: Container;
  private levelUpModal!: LevelUpModal;
  private bodyPlanOverlay!: BodyPlanOverlay;
  /** Set once the end-of-run summary opens so duplicate run-ended events
   *  (death + timer in the same frame, etc.) don't re-open it. */
  private runEnded = false;
  /** When true, update() short-circuits — used by the level-up + end-run pauses. */
  private paused = false;
  /** Queue of pending level-ups (multiple kills can spill into one frame). */
  private pendingLevelUps = 0;
  /** Biomass goal crossed — open the level-complete screen next frame. */
  private completePending = false;
  private deaths = 0;
  private apexSlain = false;
  private tutorial: CambrianTutorial | null = null;
  /** Creature the tutorial is pointing at (0 = none) + its marker ring. */
  private tutorialMark = 0;
  private tutorialMarkGfx: Graphics | null = null;
  private spotlight: CodexSpotlight | null = null;
  /** Game time of the last codex intro; intros are spaced CODEX_GAP apart. */
  private lastCodexAt = -Infinity;
  private levelComplete = new LevelCompleteModal();
  private codexBook: CambrianCodex | null = null;
  private codexPaused = false;
  private achievements: AchievementSystem | null = null;
  private touchPad: TouchPad | null = null;
  /** Kill tally by tier for the run summary. */
  private killsByTier: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0 };
  /** Off-screen apex marker (DOM element on the screen edge). */
  private domApexArrow: HTMLElement | null = null;

  // Data — loaded from /src/data/cambrian/* in M2+. M1 ships empty arrays so
  // the stage boots even before content files exist.
  private creatures: CreatureDef[] = [];

  /** Per-creature silhouette textures (CC-BY game-icons.net SVGs in
   *  /public/sprites/cambrian/). Loaded once in init; resolveSpriteTexture()
   *  maps a creature def.id to the closest match. Falls back to undefined
   *  (procedural only) if the texture failed to load. */
  private spriteTextures = new Map<string, Texture>();

  // DOM HUD.
  private domHud: HTMLElement | null = null;
  private domVersion: HTMLElement | null = null;
  private hudRefs: {
    biomass?: HTMLElement;
    hp?: HTMLElement; hpBar?: HTMLElement;
    timer?: HTMLElement; size?: HTMLElement;
    goalText?: HTMLElement; goalBar?: HTMLElement; goalNext?: HTMLElement;
    archetype?: HTMLElement;
    atpBar?: HTMLElement; atpCell?: HTMLElement; atpNotches?: HTMLElement; atp?: HTMLElement;
    frenzy?: HTMLElement; frenzyN?: HTMLElement; frenzyMult?: HTMLElement; frenzyBar?: HTMLElement;
    ecoBars?: HTMLElement[]; ecoNums?: HTMLElement[];
  } = {};
  private domEcoPanel: HTMLElement | null = null;
  private domGoalBanner: HTMLElement | null = null;
  private domGoalBannerHideAt = 0;
  private domRemovers: Array<() => void> = [];

  async init(ctx: StageContext): Promise<void> {
    this.ctx = ctx;
    this.layer = new Container();
    ctx.worldLayer.addChild(this.layer);
    ctx.worldLayer.filters = [];

    this.worldContainer = new Container();
    this.hudLayer = new Container();
    this.layer.addChild(this.worldContainer);
    this.layer.addChild(this.hudLayer);

    this.bgLayer = new Container();
    this.kelpLayer = new Container();
    this.wakeLayer = new Container();
    this.wakeGfx = new Graphics();
    // Additive blend: the wake brightens the water beneath it (like real
    // specular highlights on a moving surface) instead of painting opaque
    // foam pads on top. This is what reads as "water" vs "walking on it".
    this.wakeGfx.blendMode = 'add';
    this.wakeLayer.addChild(this.wakeGfx);
    this.creatureLayer = new Container();
    this.projectileLayer = new Container();
    this.playerLayer = new Container();
    // Order: caustic bg → kelp silhouettes → wakes → creatures → projectiles → player.
    // Wakes sit under creatures so a hunter swims OVER its own trail.
    this.worldContainer.addChild(this.bgLayer);
    this.worldContainer.addChild(this.kelpLayer);
    this.worldContainer.addChild(this.wakeLayer);
    this.worldContainer.addChild(this.creatureLayer);
    this.worldContainer.addChild(this.projectileLayer);
    this.worldContainer.addChild(this.playerLayer);

    // --- post-processing ----------------------------------------------------
    // GodrayFilter was previously applied to bgLayer — but bgLayer contains
    // the 4000×4000 bg sprite, so the filter framebuffer covered the entire
    // world (up to 16M pixels per frame) and was tanking perf. The bg.png
    // already has soft light streaks painted in, so we skip the filter
    // entirely. If we ever want godrays back, attach to a screen-sized
    // overlay quad so the framebuffer is bounded by the viewport.

    // AdvancedBloomFilter on everything that draws on top of the bg — turns
    // bright eyes / projectiles / glowing tendrils into proper glow blooms
    // and makes the whole stage feel lit instead of flat vector art.
    // Bloom is by far the heaviest filter in the stage. blur=6 quality=5
    // does many full-screen passes per frame and tanks frame rate. blur=3
    // quality=2 keeps the glow look but is ~5–8× cheaper.
    this.bloomFilter = new AdvancedBloomFilter({
      threshold: 0.5,
      bloomScale: 1.0,
      brightness: 1.0,
      blur: 3,
      quality: 2,
    });
    // Apply bloom to the per-layer stacks ABOVE the bg (so the bg has its
    // own godrays without being double-bloomed). Each layer carries its own
    // filter instance is overkill — instead bloom the whole worldContainer
    // but exclude the bg via separate layer ordering: bgLayer stays first
    // and has filters set on itself; bloom applies to the playerLayer-and-
    // sibling stacks. Simplest path: apply to the creatureLayer + projectile
    // + player layers as a group via a wrapper container.
    const bloomGroup = new Container();
    this.worldContainer.removeChild(this.creatureLayer);
    this.worldContainer.removeChild(this.projectileLayer);
    this.worldContainer.removeChild(this.playerLayer);
    bloomGroup.addChild(this.creatureLayer);
    bloomGroup.addChild(this.projectileLayer);
    bloomGroup.addChild(this.playerLayer);
    bloomGroup.filters = [this.bloomFilter];
    this.worldContainer.addChild(bloomGroup);

    // FX layer sits ABOVE the bloom group so particles + floating text
    // ride on top of the bloomed scene (bloom on a fading particle puff
    // would smear the puff into a long glow streak — undesirable).
    this.fxLayer = new Container();
    this.worldContainer.addChild(this.fxLayer);
    this.particles = new ParticleSystem(this.fxLayer);

    // Try to load data files. Falls back to empty arrays if not present.
    await this.loadStageData();
    // Load the CC-BY-3.0 silhouette textures (game-icons.net). Errors are
    // non-fatal — the procedural body is enough on its own.
    await this.loadCreatureSprites();

    // Apply stageConfig.json overrides (world size, HP, XP curve, apex window).
    this.applyStageConfig();

    // Camera zoom persists (touch players pinch to taste).
    this.worldZoom = startZoom();

    // Re-center player + camera after world resize from config.
    this.px = this.worldW / 2;
    this.py = this.worldH / 2;
    this.camX = this.px;
    this.camY = this.py;

    // Background — tide-pool palette. Floor + caustic ribbons + edge vignette.
    this.drawBackground();

    // Systems wiring.
    this.ecosystem = new EcosystemSystem(
      this.buildEcoConfig(),
      this.creatures,
      (def) => this.spawnCreatureEntity(def),
    );
    // Species unlock as you evolve (see minGrowth in creatures.json); on
    // the first run the tutorial also holds some back until introduced.
    this.createTutorial();
    this.ecosystem.setAllow((d) => (d.minGrowth ?? 0) <= this.growthIdx && (this.tutorial?.allows(d) ?? true));

    // Player setup. Layer order (bottom → top):
    //   halo         — soft glow trailing the creature
    //   sprite       — piranha silhouette (game-icons.net, tinted)
    //   overlay gfx  — procedural zone mutations on top of the sprite
    // The Graphics objects rotate together with this.facing in update().
    this.playerEntity = ctx.world.createEntity();
    ctx.world.addComponent(this.playerEntity, Position, { x: this.px, y: this.py });
    this.playerHaloGfx = new Graphics();
    this.playerLayer.addChild(this.playerHaloGfx);
    // No sprite: the player is the procedural body from draw.ts (schematic
    // yellow fish — caudal fin, dorsal stripe, gill arcs, eye — with the
    // mutation overlays transforming it). Creatures keep their silhouettes.
    this.playerSprite = null;
    this.playerGfx = new Graphics();
    this.playerLayer.addChild(this.playerGfx);
    // Faint thread joining each intact plankton line, under the creatures.
    this.lineGfx = new Graphics();
    this.creatureLayer.addChildAt(this.lineGfx, 0);

    // Run state. Three ways in:
    //   resume  — run.stageData.cambrian.live exists (Continue / reload)
    //   advance — fresh run with a Phase 2 carryover to consume
    //   jump    — fresh run, nothing to consume
    const run = saveManager.getRun();
    const live = run.stageData.cambrian?.live as CambrianLive | undefined;
    const carry = saveManager.getMeta().prestige?.carryover as Phase2Carryover | undefined;
    const fresh = !live && run.biomass === 0;
    if (live) {
      this.parts = { ...(live.parts ?? {}) };
      this.deathStreak = live.deathStreak ?? 0;
      this.recomputePassives();
      this.playerHp = Math.min(this.playerHpMax, live.hp);
      this.playerAtp = live.atp;
      this.elapsed = run.scoreTime;
      if (live.apexSpawned) this.apexSpawnAt = 0;
      this.goalReached = !!live.completed;
      if (live.killsByTier) this.killsByTier = { ...this.killsByTier, ...live.killsByTier };
      this.deaths = live.deaths ?? 0;
    } else {
      if (fresh && carry?.fromStage === 'multicell') {
        // Phase 2 → 3: DNA (kept in meta, not shown here) + biomass head start, and the cluster's make-up
        // becomes the starting body (3+ cells of a type = one mounted part).
        player.dna = carry.totalDnaCarry ?? 0;
        saveManager.getMeta().dna = player.dna;
        run.biomass = carry.biomassHeadStart ?? 0;
        const parts: string[] = [];
        if ((carry.cells?.armor ?? 0) >= 3) { this.parts.thorns = 1; parts.push('thorn spines'); }
        if ((carry.cells?.motor ?? 0) >= 3) { this.parts.fins = 1; parts.push('fins'); }
        if ((carry.cells?.sensor ?? 0) >= 3) { this.parts.quills = 1; parts.push('quills'); }
        this.introBanner = {
          title: 'CARRIED FROM PHASE 2',
          subtitle: `+${run.biomass.toLocaleString()} biomass` + (parts.length ? ` · ${parts.join(', ')}` : ''),
          durationSec: 6,
        };
      }
      // Fresh run: play the colony → fish evolution before handing over.
      // The colony is the player's real Phase 2 cluster when we have it.
      const cellsIn = carry?.cells;
      const cellList: IntroCellType[] = [];
      if (cellsIn) {
        for (let i = 0; i < (cellsIn.core ?? 0); i++) cellList.push('core');
        for (let i = 0; i < (cellsIn.armor ?? 0); i++) cellList.push('armor');
        for (let i = 0; i < (cellsIn.motor ?? 0); i++) cellList.push('motor');
        for (let i = 0; i < (cellsIn.sensor ?? 0); i++) cellList.push('sensor');
      }
      this.startIntro(cellList);
    }
    this.recomputePassives();
    this.drawPlayer(0, 0);

    // Level-up modal — queue and open in update() so we never open
    // mid-tick (avoids re-entrancy when a kill triggers level-up).
    this.levelUpModal = new LevelUpModal();
    this.bodyPlanOverlay = new BodyPlanOverlay(() => this.parts);
    // Growth replaces XP levels: part picks open on biomass thresholds
    // (see onBiomassChanged), never on kills.
    player.biomass = saveManager.getRun().biomass;
    const liveEarned = live?.evolutionsEarned;
    while (this.growthIdx < GROWTH_THRESHOLDS.length && player.biomass >= GROWTH_THRESHOLDS[this.growthIdx]) {
      this.growthIdx++;
      // A fresh run whose head start already crossed thresholds still gets
      // those body-slot picks; a resumed run made them already.
      if (!live) this.pendingLevelUps += 1;
    }
    this.evolutionsEarned = Math.max(this.growthIdx, liveEarned ?? 0);
    this.domRemovers.push(bus.on('biomass:changed', ({ value }) => this.onBiomassChanged(value)));
    this.saveLive();
    // User-pause toggles the body-plan status card. Level-up + end-of-run
    // pauses set `this.paused` directly without emitting bus events, so this
    // only fires for ESC / middle-mouse / panel-open pauses.
    this.domRemovers.push(bus.on('game:paused', () => {
      this.userPaused = true;
      this.syncCursor();
      // A codex spotlight pauses too; it has its own card.
      if (!this.spotlight?.isActive()) this.bodyPlanOverlay.open();
    }));
    this.domRemovers.push(bus.on('game:resumed', () => { this.userPaused = false; this.syncCursor(); this.bodyPlanOverlay.close(); }));
    // Run-ended handler — opens the summary on death / apex-kill / timer.
    // Run-ended summary is no longer wired: death soft-respawns (see
    // respawnAfterDeath) and the stage is won by biomass, like L1/L2.
    // Kill tally for the summary screen.
    this.domRemovers.push(bus.on('cambrian:kill', (p) => {
      if (!p.byPlayer) return;
      this.killsByTier[p.victimTier] = (this.killsByTier[p.victimTier] ?? 0) + 1;
      // Apex slain — the biggest flip of the stage. A milestone, not an end:
      // the hunt goes on toward the biomass goal.
      if (p.victimTier === 4) {
        this.apexSlain = true;
        bus.emit('cambrian:apex:killed', undefined);
        bus.emit('stage:goalBanner', {
          title: 'APEX SLAIN',
          subtitle: 'The thing that hunted you is food now.',
          durationSec: 6,
        });
      }
    }));

    // Codex: first-encounter cards for each species, introduced one at a
    // time with a gap between them (see findCodexTarget).
    const codexIcons = this.buildCodexIcons();
    this.spotlight = new CodexSpotlight(
      ctx.app, ctx.fxLayer, ctx.data.codex,
      (id) => this.findCodexTarget(id),
      codexIcons,
    );
    const book = this.creatures.map((d) => (d.codexId ? ctx.data.codex[d.codexId] : undefined)).filter((e): e is CodexEntry => !!e);
    this.codexBook = new CambrianCodex(book, codexIcons, (open) => {
      // Reading the codex pauses play (unless something else already has).
      if (open && !this.paused) { this.paused = true; this.codexPaused = true; }
      if (!open && this.codexPaused) { this.paused = false; this.codexPaused = false; }
      this.syncCursor();
    });
    this.domRemovers.push(bus.on('codex:unlocked', ({ entryId }) => {
      // Record the discovery (the codex system only lives in Phase 1).
      const meta = saveManager.getMeta();
      if (ctx.data.codex[entryId] && !meta.codexUnlocked.includes(entryId)) meta.codexUnlocked.push(entryId);
      this.codexBook?.refreshCount();
    }));
    // Achievements: this stage reports kills, evolutions, parts, frenzy,
    // perfect lines, deaths and the clear; the system grants shards.
    this.achievements = new AchievementSystem(ctx.data.achievements);
    this.achievements.notifyStageStart('cambrian');
    if (this.deaths > 0) bus.emit('player:died', undefined); // resumed run: already eaten
    this.lastCodexAt = this.elapsed - CODEX_GAP + CODEX_AFTER_TUTORIAL;
    this.tutorialMarkGfx = new Graphics();
    this.worldContainer.addChild(this.tutorialMarkGfx);
    // Resumed/no-intro runs start the tutorial straight away if it's due.
    if (!this.intro) this.maybeStartTutorial();

    // Prime ecosystem (creatures appear immediately if defs are loaded).
    this.applyPopulationMix();
    this.ecosystem.primeInitialPopulation(0.6);

    // Music — dedicated Cambrian track (Beneath_The_Liquid_Lens.mp3).
    bus.emit('music:setTrack', { id: 'cambrian' });

    // HUD shell (kept hidden while the opening cinematic plays).
    this.buildDomHud();
    if (this.intro) this.setHudVisible(false);

    // Touch: bite + both dodges as on-screen buttons (no right-click).
    this.touchPad = createTouchPad([
      { id: 'dodgeL', icon: '↰', label: 'DODGE L', side: 'left', onPress: () => this.tryFlick(-1) },
      { id: 'dodgeR', icon: '↱', label: 'DODGE R', side: 'left', onPress: () => this.tryFlick(1) },
      { id: 'bite', icon: '✸', label: 'BITE', side: 'right', primary: true, onPress: () => this.tryLunge() },
    ]);

    // Pointer input.
    this.mouseX = ctx.app.screen.width / 2;
    this.mouseY = ctx.app.screen.height / 2;
    this.onPointerMove = (e: PointerEvent) => {
      const rect = ctx.app.canvas.getBoundingClientRect();
      this.mouseX = e.clientX - rect.left;
      this.mouseY = e.clientY - rect.top;
    };
    ctx.app.canvas.addEventListener('pointermove', this.onPointerMove);

    // Lunge: left-click / tap / Space. Right-click is reserved (brake later)
    // so suppress the context menu over the canvas.
    this.onPointerDown = (e: PointerEvent) => {
      if (e.button === 2) {
        e.preventDefault();
        if (this.intro) { this.intro.advance(); return; }
        const rect = ctx.app.canvas.getBoundingClientRect();
        this.mouseX = e.clientX - rect.left;
        this.mouseY = e.clientY - rect.top;
        // Single right-click = dash right; a second one inside the window
        // turns it into a dash left. The right dash waits out the window.
        const now = performance.now();
        if (this.flickPendingAt > 0 && now - this.flickPendingAt < FLICK_DOUBLE_MS) {
          this.flickPendingAt = 0;
          this.tryFlick(-1);
        } else {
          this.flickPendingAt = now;
        }
        return;
      }
      if (e.button !== 0) return;
      e.preventDefault();
      if (this.intro) { this.intro.advance(); return; }
      const rect = ctx.app.canvas.getBoundingClientRect();
      this.mouseX = e.clientX - rect.left;
      this.mouseY = e.clientY - rect.top;
      // On touch a tap only steers — biting and dodging are on the pad.
      if (!IS_TOUCH) this.tryLunge();
    };
    this.onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (this.intro) {
        if (e.repeat) return;
        if (e.code === 'Space' || e.code === 'Enter') { e.preventDefault(); this.intro.advance(); }
        return;
      }
      if (e.repeat) return;
      if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') { e.preventDefault(); this.tryFlick(1); return; }
      if (e.code === 'KeyC' && !this.intro) { this.codexBook?.toggle(); return; }
      // Cheat: N tops biomass up to 1 short of the next evolution, so the
      // next bite triggers it the way real play does.
      if (e.code === 'KeyN') {
        if (!this.paused && this.growthIdx < GROWTH_THRESHOLDS.length) {
          const add = Math.floor(GROWTH_THRESHOLDS[this.growthIdx] - 1 - player.biomass);
          if (add > 0) {
            player.addBiomass(add);
            saveManager.getMeta().scoreProgress.unranked = true;
          }
        }
        return;
      }
      if (e.code !== 'Space') return;
      e.preventDefault();
      this.tryLunge();
    };
    this.onContextMenu = (e: MouseEvent) => { e.preventDefault(); };
    ctx.app.canvas.addEventListener('pointerdown', this.onPointerDown);
    ctx.app.canvas.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('keydown', this.onKeyDown);

    // EventBus — handle NPC kills (they fire from EcosystemSystem.runPredation).
    this.domRemovers.push(bus.on('cambrian:kill', (p) => this.onKill(p)));
    // Ecosystem bars update on every population change.
    this.domRemovers.push(bus.on('cambrian:ecosystem:changed', (p) => this.updateEcoBars(p)));
    // Fire one immediately so the bars reflect initial primed population.
    this.updateEcoBars({
      populations: {
        1: this.ecosystem.getPopulation(1), 2: this.ecosystem.getPopulation(2),
        3: this.ecosystem.getPopulation(3), 4: this.ecosystem.getPopulation(4),
      },
      caps: {
        1: this.ecosystem.getCap(1), 2: this.ecosystem.getCap(2),
        3: this.ecosystem.getCap(3), 4: this.ecosystem.getCap(4),
      },
    });

    // Camera starts on player.
    this.updateCamera();
    // Debug hook (same spirit as window.__evolve in main.ts).
    (window as any).__cambrian = this;
  }

  // ------------------------------------------------------------------
  // Stage config — pulled from JSON, falls back to module constants.
  // ------------------------------------------------------------------
  private async loadStageConfig(): Promise<void> {
    try {
      const mod = await import('@/data/cambrian/stageConfig.json');
      this.cfg = ((mod as any).default ?? mod) as StageConfig;
    } catch {
      // Fall back to a synthetic default if the JSON ever fails to load.
      this.cfg = {
        duration_seconds: 1500,
        biome_size: { w: DEFAULT_WORLD_W, h: DEFAULT_WORLD_H },
        tier_population_caps: { '1': 100, '2': 60, '3': 30, '4': 1 },
        tier_regen_rates: { '1': 4.0, '2': 1.5, '3': 0.4, '4': 0.0 },
        apex_spawn_time_range: { min_sec: 480, max_sec: 720 },
        level_up_xp_curve: DEFAULT_XP_CURVE,
        player: { hp_max: PLAYER_BASE_HP, base_speed: PLAYER_BASE_SPEED, base_radius: PLAYER_BASE_RADIUS, starting_mutation: '' },
      };
    }
  }

  private applyStageConfig(): void {
    // loadStageConfig runs as part of loadStageData below — by the time
    // we get here, this.cfg may still be null if the JSON literally hasn't
    // been loaded (rare edge case). Bail to defaults if so.
    if (!this.cfg) return;
    this.worldW = this.cfg.biome_size.w ?? DEFAULT_WORLD_W;
    this.worldH = this.cfg.biome_size.h ?? DEFAULT_WORLD_H;
    this.playerHpMax = this.cfg.player.hp_max ?? PLAYER_BASE_HP;
    this.playerHp = this.playerHpMax;
    const r = this.cfg.apex_spawn_time_range;
    this.apexSpawnAt = r.min_sec + Math.random() * (r.max_sec - r.min_sec);
  }

  private buildEcoConfig(): EcosystemConfig {
    if (!this.cfg) return DEFAULT_ECO_CONFIG;
    const caps = this.cfg.tier_population_caps;
    const regen = this.cfg.tier_regen_rates;
    return {
      1: { cap: caps['1'] ?? 100, regenPerSec: regen['1'] ?? 4.0 },
      2: { cap: caps['2'] ?? 60, regenPerSec: regen['2'] ?? 1.5 },
      3: { cap: caps['3'] ?? 30, regenPerSec: regen['3'] ?? 0.4 },
      4: { cap: caps['4'] ?? 1, regenPerSec: regen['4'] ?? 0.0 },
    };
  }

  // ------------------------------------------------------------------
  // Data loading — Vite static imports. Fall back gracefully if missing.
  // ------------------------------------------------------------------
  private async loadStageData(): Promise<void> {
    try {
      const mod = await import('@/data/cambrian/creatures.json');
      this.creatures = (mod.default ?? mod) as CreatureDef[];
    } catch {
      this.creatures = [];
    }
    await this.loadStageConfig();
  }

  /** Load the silhouette textures used by creatures + the player body. */
  private async loadCreatureSprites(): Promise<void> {
    // Most sprites are silhouette SVGs; the player uses a finished color PNG.
    const entries: Array<{ slug: string; url: string }> = [
      { slug: 'plankton',       url: '/sprites/cambrian/plankton.svg' },
      { slug: 'trilobite',      url: '/sprites/cambrian/trilobite.svg' },
      { slug: 'opabinia',       url: '/sprites/cambrian/opabinia.svg' },
      { slug: 'hunting-worm',   url: '/sprites/cambrian/hunting-worm.svg' },
      { slug: 'amplectobelua',  url: '/sprites/cambrian/amplectobelua.svg' },
      { slug: 'anomalocaris',   url: '/sprites/cambrian/anomalocaris.svg' },
      { slug: 'bg',             url: '/sprites/cambrian/bg.png' },
    ];
    const results = await Promise.all(entries.map(async ({ slug, url }) => {
      try {
        const tex = await Assets.load(url);
        if (tex) {
          this.spriteTextures.set(slug, tex);
          return { slug, ok: true, w: tex.width, h: tex.height };
        }
        return { slug, ok: false, reason: 'no texture' };
      } catch (err) {
        return { slug, ok: false, reason: String(err) };
      }
    }));
    // One concise log so the browser console makes it obvious if a sprite
    // is missing (was silent before — a 0×0 SVG just disappeared).
    console.info('[Cambrian] sprite load:', results);
  }

  /** Map a creature def id → silhouette texture slug. Multiple creatures
   *  can share a silhouette when their real-world shapes are similar
   *  (proto-trilobite + marrella + wiwaxia all read as armored arthropods). */
  private resolveSpriteSlug(defId: string): string | null {
    if (defId.startsWith('plankton') || defId === 'diatom_drift' || defId === 'ostracod') return 'plankton';
    if (defId === 'proto_trilobite' || defId === 'marrella' || defId === 'wiwaxia') return 'trilobite';
    if (defId === 'opabinia') return 'opabinia';
    if (defId === 'hunting_worm' || defId === 'hallucigenia') return 'hunting-worm';
    if (defId === 'amplectobelua') return 'amplectobelua';
    if (defId === 'anomalocaris') return 'anomalocaris';
    return null;
  }

  // ------------------------------------------------------------------
  // Background — tiled seafloor texture (bg.png) with a caustic shader
  // overlay for animated water shimmer + the player's warm halo. Kelp
  // sprites sit on a layer above. All world-anchored so camera scroll
  // is handled by the parent container's transform.
  // ------------------------------------------------------------------
  private drawBackground(): void {
    const bgTex = this.spriteTextures.get('bg');
    if (bgTex) {
      // Stretch one instance over the whole world instead of tiling. bg.png
      // isn't perfectly seamless and tiling produced visible boundary lines.
      // A ~2× upscale on a painterly seafloor is unnoticeable in motion.
      const bg = new Sprite(bgTex);
      bg.width  = this.worldW;
      bg.height = this.worldH;
      this.bgLayer.addChild(bg);
    }
    this.causticMesh = new CausticSeaMesh(this.worldW, this.worldH);
    // Alpha is baked into the shader's finalColor (Mesh ignores
    // DisplayObject.alpha for custom shaders) — see causticSea.ts.
    this.bgLayer.addChild(this.causticMesh);
    this.scatterKelp(28);
  }

  private scatterKelp(count: number): void {
    // Sparse, deterministic-ish placement: rejection-sample on a grid so
    // kelp doesn't bunch up. Each fronds gets a phase offset so the field
    // sways like a real seabed instead of a metronome.
    const margin = 120;
    for (let i = 0; i < count; i++) {
      const x = margin + Math.random() * (this.worldW - margin * 2);
      const y = margin + Math.random() * (this.worldH - margin * 2);
      const h = 110 + Math.random() * 200;
      // Tint: deeper kelp at the bottom of the world, brighter near the top.
      const yFrac = y / this.worldH;
      const baseR = Math.round(0x2a + (1 - yFrac) * 0x40);
      const baseG = Math.round(0x6c + (1 - yFrac) * 0x40);
      const baseB = Math.round(0x44 + (1 - yFrac) * 0x18);
      const tint = (baseR << 16) | (baseG << 8) | baseB;
      const g = new Graphics();
      g.position.set(x, y);
      this.kelpLayer.addChild(g);
      const inst = { g, baseX: x, baseY: y, baseH: h, phase: Math.random() * Math.PI * 2, tint };
      this.kelpInstances.push(inst);
      this.drawKelp(inst, 0);
    }
  }

  /** Redraw one kelp instance for the current sway phase. */
  private drawKelp(k: { g: Graphics; baseH: number; phase: number; tint: number }, t: number): void {
    const g = k.g;
    g.clear();
    // Frond described by N stacked segments, each offset horizontally by
    // a sin wave with the kelp's own phase. Bottom segment is wide; top
    // tapers to a thin tip.
    const SEGMENTS = 5;
    const baseWidth = 16;
    const phase = k.phase + t * 0.7;
    let prevX = 0;
    let prevY = 0;
    for (let i = 1; i <= SEGMENTS; i++) {
      const f = i / SEGMENTS;
      const segH = k.baseH * f;
      const offX = Math.sin(phase + f * 1.6) * (10 + f * 22);
      const x = offX;
      const y = -segH;
      const w = baseWidth * (1 - f * 0.7);
      g.moveTo(prevX - w * 0.5, prevY)
        .lineTo(x - w * 0.5, y)
        .lineTo(x + w * 0.5, y)
        .lineTo(prevX + w * 0.5, prevY)
        .fill({ color: k.tint, alpha: 0.55 });
      prevX = x;
      prevY = y;
    }
    // Small bulb at the top.
    g.circle(prevX, prevY, 4).fill({ color: k.tint, alpha: 0.7 });
  }

  // ------------------------------------------------------------------
  // Player composite — delegated to draw.ts (`drawPlayerComposite`).
  // The composite has anatomical zones (cephalon / pygidium / flanks /
  // dorsal ridge / ventral belly) and each equipped mutation transforms
  // the zone it's mounted in: predator-front grows visible jaws,
  // armored-flank grows segmented plates, symbiote-dorsal grows polyp
  // colonies, etc. See draw.ts for the per-zone overlays.
  // ------------------------------------------------------------------
  private drawPlayer(t: number, speed: number): void {
    this.partFx.lunge = this.lungeT > 0 ? this.lungeT / LUNGE_DURATION : 0;
    const ctx: PlayerDrawCtx = { r: this.playerRadius, t, speed, parts: this.parts, fx: this.partFx };
    drawPlayerHalo(this.playerHaloGfx, ctx);
    drawPlayerComposite(this.playerGfx, ctx);
    // Family tint colour-shifts the silhouette so committed builds aren't
    // monochrome. Same blend the procedural body uses.
    if (this.playerSprite) {
      // Breathe + swim squash: tail-beat compresses the body laterally and
      // stretches it forward (sx & sy out of phase), driven by the same
      // wag clock as the rotation so it stays in sync.
      const maxSpeed = PLAYER_BASE_SPEED * (1 + this.passives.moveSpeedMult);
      const speedFrac = Math.min(1, Math.hypot(this.pvx, this.pvy) / Math.max(1, maxSpeed));
      const wagFreq = 2.5 + speedFrac * 4.5;
      const beat = Math.cos(this.elapsed * wagFreq);
      const breathe = 1.0 + Math.sin(t * 2.0) * 0.04;
      const stretch = 0.06 * speedFrac * (1 - this.turnIntensity);
      const base = this.playerSpriteBaseScale * this.sizeScale * breathe;
      // Lunge: the body stretches along its long axis and narrows, easing
      // out over the burst so the bite reads as a snap, not a slide.
      const lunge = this.lungeT > 0 ? 0.22 * (this.lungeT / LUNGE_DURATION) : 0;
      // Sprite is head-right before rotation, so x is the body's long axis.
      this.playerSprite.scale.set(
        base * (1 + beat * stretch + lunge),
        base * (1 - beat * stretch - lunge * 0.6),
      );
    }
  }

  /** Fold the grown parts' levels into the numbers the sim reads. */
  private recomputePassives(): void {
    const lv = (id: PartId): number => partLevel(this.parts, id);
    const S = PART_STATS;
    this.passives = {
      damageMult: 1.0,
      moveSpeedMult: S.finsSpeed[lv('fins')],
      hpRegen: S.gillsRegen[lv('gills')],
      biomassMult: 1.0,
      lungeSpeedMult: S.tailLungeMult[lv('tail')],
      atpMaxMult: S.tailAtpMaxMult[lv('tail')],
      lungeCooldownMult: S.jawsCooldownMult[lv('jaws')],
      atpRegenMult: S.tailAtpRegenMult[lv('tail')],
    };
    this.biteDamage = BITE_BASE_DAMAGE * S.jawsBiteMult[lv('jaws')];
    const atpMax = ATP_MAX * this.passives.atpMaxMult;
    if (atpMax > this.atpMax) this.playerAtp += atpMax - this.atpMax;
    this.atpMax = atpMax;
    this.playerAtp = Math.min(this.playerAtp, this.atpMax);
    const hpMax = (this.cfg?.player?.hp_max ?? PLAYER_BASE_HP) + S.gillsHp[lv('gills')];
    // Growing gills fills the new capacity too.
    if (hpMax > this.playerHpMax) this.playerHp += hpMax - this.playerHpMax;
    this.playerHpMax = hpMax;
    this.playerHp = Math.min(this.playerHp, this.playerHpMax);
    const charges = S.shieldCharges[lv('shield')];
    if (charges > 0 && this.partFx.shieldCharges === 0 && this.shieldRechargeT <= 0) this.partFx.shieldCharges = charges;
  }

  /** Grow a new part or upgrade one (level +1). */
  private growPart(id: PartId): void {
    const before = partLevel(this.parts, id);
    this.parts[id] = before + 1;
    const maxed = Object.values(this.parts).filter((lv) => (lv ?? 0) >= MAX_PART_LEVEL).length;
    bus.emit('cambrian:partGrown', { partId: id, level: before + 1, maxed });
    this.recomputePassives();
    // A fresh shield / new capacity comes fully charged.
    if (id === 'shield') { this.partFx.shieldCharges = PART_STATS.shieldCharges[before + 1]; this.shieldRechargeT = 0; }
    bus.emit('fx:particles', { x: this.px, y: this.py, count: 28, color: 0xffe080, spread: 90 });
    bus.emit('fx:flash', { color: 0xffe0a0, alpha: 0.25, duration: 0.4 });
    this.saveLive();
  }

  // ------------------------------------------------------------------
  // Lunge + frenzy.
  // ------------------------------------------------------------------
  private frenzyBiomassMult(): number {
    return Math.min(FRENZY_BIOMASS_CAP, 1 + this.frenzy * FRENZY_BIOMASS_PER_STACK);
  }
  private frenzySpeedBonus(): number {
    return Math.min(FRENZY_SPEED_CAP, this.frenzy * FRENZY_SPEED_PER_STACK);
  }
  private frenzyCooldownMult(): number {
    return Math.max(FRENZY_COOLDOWN_FLOOR, 1 - this.frenzy * FRENZY_COOLDOWN_PER_STACK);
  }

  private tryLunge(): void {
    if (this.paused || this.dead || this.runEnded) return;
    if (this.lungeT > 0 || this.lungeCooldownT > 0) return;
    if (this.playerAtp < LUNGE_ATP_COST) {
      bus.emit('audio:cue', { id: 'damage', volume: 0.12 });
      return;
    }
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;
    let dx = this.mouseX - sw / 2;
    let dy = this.mouseY - sh / 2;
    const d = Math.hypot(dx, dy);
    if (d < 4) { dx = Math.cos(this.facing); dy = Math.sin(this.facing); }
    else { dx /= d; dy /= d; }
    this.lungeDirX = dx;
    this.lungeDirY = dy;
    this.lungeT = LUNGE_DURATION;
    this.lungeCooldownT = LUNGE_COOLDOWN * this.frenzyCooldownMult() * this.passives.lungeCooldownMult;
    this.lungeHitSet.clear();
    this.playerAtp -= LUNGE_ATP_COST;
    this.atpRegenDelayT = ATP_REGEN_DELAY;
    bus.emit('audio:cue', { id: 'dash', volume: 0.4 });
    // Launch splash: a burst of glints kicked back from the tail.
    for (let i = 0; i < 3; i++) this.spawnBowSplash(this.px, this.py, Math.atan2(dy, dx), this.playerRadius, 1);
    bus.emit('fx:particles', {
      x: this.px - dx * this.playerRadius, y: this.py - dy * this.playerRadius,
      count: 8, color: 0xd8f4ff, spread: 40,
    });
  }

  /**
   * Silt flick: a sideways dart — right-click right, double right-click left — with i-frames, leaving a silt cloud behind. Hunters that
   * run into the cloud lose the chase.
   */
  /** Real-time stamp of a right-click waiting to see if a second follows. */
  private flickPendingAt = 0;
  private tickPendingFlick(): void {
    if (this.flickPendingAt > 0 && performance.now() - this.flickPendingAt >= FLICK_DOUBLE_MS) {
      this.flickPendingAt = 0;
      this.tryFlick(1);
    }
  }

  private tryFlick(side: 1 | -1): void {
    if (this.paused || this.dead || this.runEnded || this.intro) return;
    if (this.flickT > 0 || this.flickCooldownT > 0) return;
    if (this.playerAtp < FLICK_ATP_COST) {
      bus.emit('audio:cue', { id: 'damage', volume: 0.12 });
      return;
    }
    // Sidestep straight out to the fish's right (side 1) or left (-1).
    const dx = -Math.sin(this.facing) * side;
    const dy = Math.cos(this.facing) * side;
    this.flickDirX = dx;
    this.flickDirY = dy;
    this.flickT = FLICK_DURATION;
    this.flickCooldownT = FLICK_COOLDOWN;
    this.lungeT = 0;
    this.invulnT = Math.max(this.invulnT, FLICK_IFRAMES);
    this.playerAtp -= FLICK_ATP_COST;
    this.atpRegenDelayT = ATP_REGEN_DELAY;
    this.tutorial?.report({ kind: 'flick', side });
    // Silt cloud where we were.
    const g = new Graphics();
    for (let i = 0; i < 9; i++) {
      const a = Math.random() * Math.PI * 2;
      const rr = Math.random() * SILT_RADIUS * 0.6;
      g.circle(Math.cos(a) * rr, Math.sin(a) * rr, SILT_RADIUS * (0.35 + Math.random() * 0.3))
        .fill({ color: 0xcdbf9a, alpha: 0.16 });
    }
    g.circle(0, 0, SILT_RADIUS * 0.5).fill({ color: 0xe8dcb8, alpha: 0.14 });
    g.position.set(this.px, this.py);
    this.creatureLayer.addChildAt(g, 0);
    this.siltClouds.push({ g, x: this.px, y: this.py, r: SILT_RADIUS, age: 0 });
    bus.emit('audio:cue', { id: 'pulse', volume: 0.35 });
    bus.emit('fx:particles', { x: this.px, y: this.py, count: 14, color: 0xd8cfae, spread: 70 });
    bus.emit('fx:screenShake', { intensity: 2, duration: 0.12 });
  }

  private tickSilt(dt: number): void {
    if (this.siltClouds.length === 0) return;
    this.siltClouds = this.siltClouds.filter((s) => {
      s.age += dt;
      const k = s.age / SILT_LIFE;
      if (k >= 1) { s.g.parent?.removeChild(s.g); this.reap.push(s.g); return false; }
      s.g.alpha = k < 0.15 ? k / 0.15 : 1 - (k - 0.15) / 0.85;
      s.g.scale.set(1 + k * 0.35);
      return true;
    });
  }

  /** Bite anything the body passes through during the lunge burst. */
  private tickLungeBites(): void {
    if (this.lungeT <= 0) return;
    const w = this.ctx.world;
    for (const e of w.query([CreatureComp, Position])) {
      if (this.lungeHitSet.has(e)) continue;
      const c = w.get(e, CreatureComp)!;
      const p = w.get(e, Position)!;
      const dx = p.x - this.px;
      const dy = p.y - this.py;
      const r = this.playerRadius + c.size + 6;
      if (dx * dx + dy * dy > r * r) continue;
      this.lungeHitSet.add(e);

      // Size flip: too big to bite through yet. The bite glances, you bounce
      // off, and if it's a hunter it now knows exactly where you are.
      if (c.tier > this.maxBiteTier) {
        const sting = partLevel(this.parts, 'stinger');
        if (sting > 0) {
          // Stinger: a real hit on something bigger, plus venom over time.
          bus.emit('fx:floatingText', { x: p.x, y: p.y - 30, text: 'STING!', color: 0xff70a0 });
          bus.emit('fx:particles', { x: p.x, y: p.y, count: 14, color: 0xe070ff, spread: 60 });
          bus.emit('audio:cue', { id: 'damage', volume: 0.3 });
          bus.emit('fx:screenShake', { intensity: 5, duration: 0.2 });
          this.partFx.stingFlash = 1;
          c.venomT = VENOM_SEC;
          c.venomDps = PART_STATS.stingVenomDps[sting];
          this.damageCreature(e, PART_STATS.stingDamage[sting]);
        } else {
          bus.emit('fx:floatingText', { x: p.x, y: p.y - 30, text: 'TOO BIG', color: 0xff8080 });
          bus.emit('fx:particles', { x: p.x, y: p.y, count: 6, color: 0xffffff, spread: 40 });
          bus.emit('audio:cue', { id: 'damage', volume: 0.25 });
          bus.emit('fx:screenShake', { intensity: 4, duration: 0.2 });
          this.damageCreature(e, this.biteDamage * GLANCE_DAMAGE_FRAC);
        }
        if (c.behavior !== 'passive') c.heading = Math.atan2(this.py - p.y, this.px - p.x);
        this.lungeT = 0;
        this.pvx = -this.lungeDirX * 320;
        this.pvy = -this.lungeDirY * 320;
        return;
      }

      // Ambush: we came in along the prey's own heading (from behind).
      const ambush = Math.cos(c.heading) * this.lungeDirX + Math.sin(c.heading) * this.lungeDirY > AMBUSH_DOT;
      const dmg = this.biteDamage * (ambush ? AMBUSH_MULT : 1);
      // Shove the prey along the bite so the impact has weight.
      p.x += this.lungeDirX * 18;
      p.y += this.lungeDirY * 18;
      // Juice scales with the prey: plankton bites stay light so a fast
      // chain doesn't stutter (no hit-stop, no shake); bigger prey crunch.
      const big = c.tier >= 2;
      bus.emit('fx:particles', { x: p.x, y: p.y, count: ambush ? 14 : big ? 10 : 5, color: c.color, spread: ambush ? 90 : 55 });
      bus.emit('audio:pop', { pitch: ambush ? 0.45 : 0.6, volume: big ? 0.32 : 0.2 });
      if (big || ambush) {
        bus.emit('fx:screenShake', { intensity: ambush ? 6 : 3, duration: 0.2 });
        this.hitStopT = Math.max(this.hitStopT, ambush ? 0.06 : 0.035);
      }
      if (ambush) {
        bus.emit('fx:floatingText', { x: p.x, y: p.y - 30, text: 'AMBUSH ×2', color: 0xffd070 });
      }
      this.damageCreature(e, dmg, true);

      // Bit a hunter that survived: bounce off it and end the lunge, so
      // lunging into a hunter is a gamble, not a free pass.
      if (w.exists(e) && c.tier >= 3) {
        this.lungeT = 0;
        this.pvx = -this.lungeDirX * 260;
        this.pvy = -this.lungeDirY * 260;
        return;
      }
    }
  }

  /**
   * Population mix follows the player's size: a hatchling sea is mostly
   * prey with a few hunters; hunters fill in as the player grows into them.
   * Caps come from stageConfig; these are the fractions applied on top.
   */
  private applyPopulationMix(): void {
    const g = this.growthIdx;
    const t2 = g >= 2 ? 1 : g >= 1 ? 0.55 : 0.35;
    const t3 = g >= 5 ? 1 : g >= 2 ? 0.5 : g >= 1 ? 0.3 : 0.18;
    const t1 = g >= 5 ? 1 : 1.4;
    this.ecosystem.setCap(1, Math.round((this.cfg?.tier_population_caps?.['1'] ?? 100) * t1));
    this.ecosystem.setCap(2, Math.round((this.cfg?.tier_population_caps?.['2'] ?? 60) * t2));
    this.ecosystem.setCap(3, Math.round((this.cfg?.tier_population_caps?.['3'] ?? 30) * t3));
  }

  /** Biomass moved: persist it, cross growth thresholds, check the goal. */
  private onBiomassChanged(value: number): void {
    saveManager.getRun().biomass = value;
    // Losing biomass (death tax) shrinks you back; mounted parts stay.
    while (this.growthIdx > 0 && value < GROWTH_THRESHOLDS[this.growthIdx - 1]) this.growthIdx--;
    while (this.growthIdx < GROWTH_THRESHOLDS.length && value >= GROWTH_THRESHOLDS[this.growthIdx]) {
      const before = this.maxBiteTier;
      this.growthIdx++;
      // Climbing back to a size you'd already reached (after deaths) grows
      // you again but doesn't hand out another part pick.
      const fresh = this.growthIdx > this.evolutionsEarned;
      if (fresh) {
        this.evolutionsEarned = this.growthIdx;
        this.pendingLevelUps += 1;
        bus.emit('cambrian:evolved', { count: this.evolutionsEarned });
      }
      this.deathStreak = 0;
      const flipped = this.maxBiteTier > before;
      bus.emit('stage:goalBanner', {
        title: fresh ? 'YOU EVOLVED' : 'SIZE REGAINED',
        subtitle: flipped
          ? `Tier ${this.maxBiteTier} creatures are now prey`
          : fresh ? 'Choose a new body part or an upgrade' : 'Keep eating to reach your next evolution',
        durationSec: 3,
      });
      bus.emit('audio:cue', { id: 'milestone', volume: 0.55 });
      bus.emit('fx:particles', { x: this.px, y: this.py, count: 30, color: 0xffd070, spread: 140 });
      bus.emit('fx:timeSlow', { factor: 0.5, duration: 0.5 });
    }
    this.applyPopulationMix();
    if (!this.goalReached && value >= STAGE_GOAL_BIOMASS) {
      this.goalReached = true;
      this.completePending = true;
      this.saveLive();
      bus.emit('audio:cue', { id: 'milestone', volume: 0.8 });
      bus.emit('fx:flash', { color: 0xffd070, alpha: 0.35, duration: 0.5 });
      bus.emit('fx:timeSlow', { factor: 0.3, duration: 1.2 });
    }
  }

  // ------------------------------------------------------------------
  // Opening cinematic.
  // ------------------------------------------------------------------
  private startIntro(cells: IntroCellType[]): void {
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;
    this.intro = new EvolutionIntro({
      width: sw, height: sh, cells,
      drawFish: (g, r, t) => drawPlayerComposite(g, { r, t, speed: 0, parts: this.parts }),
      drawHalo: (g, r, t) => drawPlayerHalo(g, { r, t, speed: 0, parts: {} }),
      onComplete: () => this.completeIntro(),
    });
    // Screen-fixed: the intro lives on the app stage above the world.
    this.ctx.uiLayer.addChild(this.intro.container);
    this.setHudVisible(false);
  }

  private completeIntro(): void {
    if (!this.intro) return;
    this.intro.destroy();
    this.intro = null;
    this.setHudVisible(true);
    bus.emit('fx:particles', { x: this.px, y: this.py, count: 36, color: 0xffd070, spread: 160 });
    bus.emit('audio:cue', { id: 'milestone', volume: 0.5 });
    if (this.introBanner) {
      bus.emit('stage:goalBanner', this.introBanner);
      this.introBanner = null;
    } else {
      bus.emit('stage:goalBanner', {
        title: 'REACH 6,000,000 BIOMASS',
        subtitle: 'Lunge to bite. Grow, and the hunters become prey.',
        durationSec: 6,
      });
    }
    this.maybeStartTutorial();
  }

  /** First run only: the sea is eased in phase by phase (see CambrianTutorial). */
  private createTutorial(): void {
    if (this.tutorial || saveManager.getMeta().cambrianTutorialDone) return;
    this.tutorial = new CambrianTutorial({
      spawnNear: (defId) => this.spawnNearPlayer(defId),
      mark: (id) => { this.tutorialMark = id; },
      slowMo: () => bus.emit('fx:timeSlow', { factor: 0.3, duration: 1.4 }),
      onDone: () => {
        saveManager.getMeta().cambrianTutorialDone = true;
        saveManager.save();
        this.lastCodexAt = this.elapsed - CODEX_GAP + CODEX_AFTER_TUTORIAL;
      },
    });
  }

  private maybeStartTutorial(): void {
    this.tutorial?.start();
  }

  /** Spawn one creature a short swim ahead of the player (tutorial props). */
  private spawnNearPlayer(defId: string): number {
    const id = this.ecosystem.spawnSpecific(defId);
    const p = id > 0 ? this.ctx.world.get(id, Position) : undefined;
    if (p) {
      const a = this.facing + (Math.random() - 0.5) * 1.2;
      p.x = Math.max(120, Math.min(this.worldW - 120, this.px + Math.cos(a) * 200));
      p.y = Math.max(120, Math.min(this.worldH - 120, this.py + Math.sin(a) * 200));
    }
    return id;
  }

  /** Pulsing ring + arrow over the creature the tutorial is talking about. */
  private drawTutorialMark(): void {
    const g = this.tutorialMarkGfx;
    if (!g) return;
    g.clear();
    const id = this.tutorialMark;
    const p = id && this.ctx.world.exists(id) ? this.ctx.world.get(id, Position) : undefined;
    if (!p) return;
    const t = this.kelpT;
    const r = 26 + Math.sin(t * 5) * 4;
    g.circle(p.x, p.y, r).stroke({ color: 0xffe080, width: 2.5, alpha: 0.9 });
    g.circle(p.x, p.y, r + 8).stroke({ color: 0xffe080, width: 1, alpha: 0.4 });
    const bob = Math.sin(t * 6) * 5;
    g.poly([p.x, p.y - r - 6 + bob, p.x - 9, p.y - r - 22 + bob, p.x + 9, p.y - r - 22 + bob]).fill({ color: 0xffe080, alpha: 0.95 });
  }

  /**
   * Codex finder: screen position of a visible creature with this codex
   * id — but only after the tutorial, and at most one new species every
   * CODEX_GAP seconds of play so intros trickle in.
   */
  private findCodexTarget(codexId: string): SpotlightTarget | null {
    if (!codexId.startsWith('cambrian.') || this.intro || this.paused || this.userPaused) return null;
    // During the tutorial each newcomer's card shows as it's introduced;
    // afterwards species trickle in one per CODEX_GAP.
    if (this.tutorial?.active) {
      if (!this.tutorial.codexAllowed(codexId) || this.elapsed - this.lastCodexAt < 4) return null;
    } else if (this.elapsed - this.lastCodexAt < CODEX_GAP) return null;
    const w = this.ctx.world;
    const off = this.worldContainer.position;
    const sw = this.ctx.app.screen.width, sh = this.ctx.app.screen.height;
    for (const e of w.query([CreatureComp, Position])) {
      const c = w.get(e, CreatureComp)!;
      if (c.codexId !== codexId) continue;
      const p = w.get(e, Position)!;
      const x = p.x * this.worldZoom + off.x, y = p.y * this.worldZoom + off.y;
      if (x < 60 || y < 60 || x > sw - 60 || y > sh - 60) continue;
      this.lastCodexAt = this.elapsed;
      return { x, y, radius: c.size * this.worldZoom };
    }
    return null;
  }

  /** Portrait of each species for the codex cards / panel. */
  private buildCodexIcons(): Map<string, HTMLCanvasElement> {
    const out = new Map<string, HTMLCanvasElement>();
    const SIZE = 64;
    for (const def of this.creatures) {
      if (!def.codexId) continue;
      const c = new Container();
      const frame = new Graphics();
      frame.rect(-SIZE / 2, -SIZE / 2, SIZE, SIZE).fill({ color: 0, alpha: 0.001 });
      c.addChild(frame);
      const g = new Graphics();
      this.drawCreature(g, def, 1, 0, -Math.PI / 4);
      // Fit the creature's reach (~2.4× its size) into the frame.
      g.scale.set(Math.min(2.2, (SIZE * 0.42) / (def.size * 2.4)));
      c.addChild(g);
      try {
        out.set(def.codexId, this.ctx.app.renderer.extract.canvas(c) as unknown as HTMLCanvasElement);
      } catch { /* icon is optional */ }
      c.destroy({ children: true });
    }
    return out;
  }

  private setHudVisible(v: boolean): void {
    for (const el of [this.domHud, this.domEcoPanel]) {
      if (!el) continue;
      el.style.transition = 'opacity 0.5s ease';
      el.style.opacity = v ? '1' : '0';
    }
  }

  /**
   * Per-frame part upkeep: quill volleys at random enemies, shield
   * recharge, venom ticking on stung creatures, and flash decay.
   */
  private tickParts(dt: number): void {
    const w = this.ctx.world;
    const fx = this.partFx;
    const decay = dt / 0.35;
    fx.stingFlash = Math.max(0, fx.stingFlash - decay);
    fx.thornFlash = Math.max(0, fx.thornFlash - decay);
    fx.shieldFlash = Math.max(0, fx.shieldFlash - dt / 0.5);

    // Venom: stung creatures bleed out over a few seconds.
    const venomed: Array<[number, number]> = [];
    for (const e of w.query([CreatureComp])) {
      const c = w.get(e, CreatureComp)!;
      if (!c.venomT || c.venomT <= 0) continue;
      c.venomT -= dt;
      venomed.push([e, (c.venomDps ?? 0) * dt]);
    }
    for (const [e, d] of venomed) this.damageCreature(e, d);

    // Shield recharge, one charge at a time.
    const shieldLv = partLevel(this.parts, 'shield');
    const maxCharges = PART_STATS.shieldCharges[shieldLv];
    if (fx.shieldCharges < maxCharges) {
      this.shieldRechargeT -= dt;
      if (this.shieldRechargeT <= 0) {
        fx.shieldCharges += 1;
        this.shieldRechargeT = fx.shieldCharges < maxCharges ? PART_STATS.shieldRecharge[shieldLv] : 0;
        bus.emit('fx:particles', { x: this.px, y: this.py, count: 10, color: 0xa8d0ff, spread: 50 });
      }
    }

    // Quills: a volley at random enemies in range once loaded.
    const q = partLevel(this.parts, 'quills');
    if (q > 0) {
      const interval = PART_STATS.quillInterval[q];
      this.quillT = Math.max(0, this.quillT - dt);
      fx.quillLoad = 1 - this.quillT / interval;
      if (this.quillT <= 0) {
        const maxTier = this.maxBiteTier;
        const enemies: Array<{ x: number; y: number }> = [];
        const prey: Array<{ x: number; y: number }> = [];
        for (const e of w.query([CreatureComp, Position])) {
          const c = w.get(e, CreatureComp)!;
          const p = w.get(e, Position)!;
          const dx = p.x - this.px, dy = p.y - this.py;
          if (dx * dx + dy * dy > QUILL_RANGE * QUILL_RANGE) continue;
          const def = this.creatures.find((d) => d.id === c.defId);
          if (def?.effect) continue;
          (c.tier >= maxTier && c.tier > 1 ? enemies : prey).push(p);
        }
        const pool = enemies.length ? enemies : prey;
        if (pool.length) {
          for (let i = 0; i < PART_STATS.quillCount[q]; i++) {
            const tgt = pool[Math.floor(Math.random() * pool.length)];
            this.fireQuill(tgt.x, tgt.y, PART_STATS.quillDamage[q]);
          }
          this.quillT = interval;
          bus.emit('audio:pop', { pitch: 1.1 + Math.random() * 0.3, volume: 0.14 });
        }
      }
    } else {
      fx.quillLoad = 1;
    }
  }

  /** Launch one quill from the back toward (tx, ty). */
  private fireQuill(tx: number, ty: number, damage: number): void {
    const ox = this.px - Math.cos(this.bodyFacing) * this.playerRadius * 0.4;
    const oy = this.py - Math.sin(this.bodyFacing) * this.playerRadius * 0.4;
    const a = Math.atan2(ty - oy, tx - ox) + (Math.random() - 0.5) * 0.12;
    const w = this.ctx.world;
    const e = w.createEntity();
    w.addComponent(e, Position, { x: ox, y: oy });
    w.addComponent(e, ProjectileComp, {
      ownerId: this.playerEntity,
      vx: Math.cos(a) * QUILL_SPEED,
      vy: Math.sin(a) * QUILL_SPEED,
      damage,
      lifetime: (QUILL_RANGE * 1.2) / QUILL_SPEED,
      age: 0, pierce: 1, hitSet: new Set<number>(),
      color: 0xd0a0ff, size: 5, kind: 'projectile',
    });
    // A slim quill with a white tip, pointing along its flight.
    const g = new Graphics();
    g.moveTo(-9, 0).lineTo(7, 0).stroke({ color: 0x8050c0, width: 3, alpha: 0.9 });
    g.moveTo(-9, 0).lineTo(7, 0).stroke({ color: 0xd0a0ff, width: 1.6 });
    g.poly([7, -2.2, 12, 0, 7, 2.2]).fill({ color: 0xffffff });
    g.position.set(ox, oy);
    g.rotation = a;
    (g as any).__entity = e;
    this.projectileLayer.addChild(g);
  }

  private breakFrenzy(): void {
    if (this.frenzy >= 3) {
      bus.emit('fx:floatingText', { x: this.px, y: this.py - 40, text: 'FRENZY BROKEN', color: 0xff8080 });
    }
    this.frenzy = 0;
    this.frenzyT = 0;
  }

  // ------------------------------------------------------------------
  // Creature factory — called by EcosystemSystem when it wants a new
  // creature in the world. Returns the new entity id.
  // ------------------------------------------------------------------
  private spawnCreatureEntity(def: CreatureDef): number {
    const w = this.ctx.world;
    const e = w.createEntity();
    const margin = 200;
    // Spawn off-screen-ish around the player so a freshly cleared area
    // re-populates from the edges rather than popping in front of the face.
    const angle = Math.random() * Math.PI * 2;
    const dist = 400 + Math.random() * 700;
    let x = this.px + Math.cos(angle) * dist;
    let y = this.py + Math.sin(angle) * dist;
    let heading = Math.random() * Math.PI * 2;
    let line: { lineId: number; lineIdx: number; lineT: number } | null = null;
    if (def.tier === 1 && !def.effect) {
      // Plankton spawn in lines: the first member anchors a new line, the
      // next LINE_LEN-1 spawns fill it in, all one species, all swimming the
      // same way so the formation holds until it starts to drift.
      let cur = this.currentLine;
      if (!cur || cur.n >= LINE_LEN) {
        cur = { id: ++this.lineSeq, x, y, dir: heading, n: 0, defId: def.id };
        this.currentLine = cur;
        this.lines.set(cur.id, { ids: [], bornAt: this.elapsed });
      }
      const lineDef = this.creatures.find((d) => d.id === cur.defId);
      if (lineDef) def = lineDef;
      x = cur.x + Math.cos(cur.dir) * cur.n * LINE_SPACING;
      y = cur.y + Math.sin(cur.dir) * cur.n * LINE_SPACING;
      heading = cur.dir;
      line = { lineId: cur.id, lineIdx: cur.n, lineT: this.elapsed };
      cur.n++;
      let entry = this.lines.get(cur.id);
      if (!entry) { entry = { ids: [], bornAt: this.elapsed }; this.lines.set(cur.id, entry); }
      entry.ids.push(e);
    }
    x = Math.max(margin, Math.min(this.worldW - margin, x));
    y = Math.max(margin, Math.min(this.worldH - margin, y));
    w.addComponent(e, Position, { x, y });
    // The sea keeps pace with you: every evolution, new bigger creatures
    // come in larger, tougher and harder-hitting (and worth more).
    const k = def.tier >= 2 ? 1 + DANGER_PER_GROWTH * this.growthIdx : 1;
    const c = {
      defId: def.id,
      tier: def.tier,
      hp: def.hp * k * k,
      maxHp: def.hp * k * k,
      speed: def.speed,
      damage: def.damage * k,
      scale: k,
      behavior: def.behavior,
      eatsTiers: def.eatsTiers,
      color: def.color,
      size: def.size * k,
      heading,
      ai: 0,
      target: 0,
      codexId: def.codexId,
      phase: Math.random() * Math.PI * 2,
      ...(line ?? {}),
    };
    w.addComponent(e, CreatureComp, c);
    // Drawable — Container with two children:
    //   body    : Graphics, procedural anatomy (animated). Runs even when
    //             no sprite is loaded so the creature still has motion.
    //   sprite  : Sprite from /public/sprites/cambrian (when available),
    //             tinted to the creature's color. Sprite gives the
    //             instantly recognizable silhouette; body adds animated
    //             organic motion underneath.
    const container = new Container();
    container.position.set(x, y);
    container.scale.set(k);
    container.eventMode = 'none';
    const body = new Graphics();
    this.drawCreature(body, def, 0, c.phase, 0);
    container.addChild(body);
    // Procedural anatomy only — the silhouette sprites on top of it made
    // creatures read as blobs. Each species has its own draw (see drawCreature).
    (container as any).__entity = e;
    (container as any).__body = body;
    this.creatureLayer.addChild(container);
    return e;
  }

  /** Per-tier creature draw — delegates to draw.ts for real Cambrian anatomy. */
  private drawCreature(g: Graphics, def: CreatureDef, t: number, phase: number, heading: number): void {
    const ctx: CreatureDrawCtx = {
      r: def.size, t, phase, heading, color: def.color,
    };
    switch (def.id) {
      case 'plankton_bloom': drawCopepod(g, ctx); return;
      case 'diatom_drift': drawDiatom(g, ctx); return;
      case 'ostracod': drawOstracod(g, ctx); return;
      case 'glow_medusa': drawGlowMedusa(g, ctx); return;
      case 'toxic_siphon': drawToxicSiphon(g, ctx); return;
      case 'opabinia': drawOpabinia(g, ctx); return;
      case 'marrella': drawMarrella(g, ctx); return;
      case 'wiwaxia': drawWiwaxia(g, ctx); return;
      case 'hallucigenia': drawHallucigenia(g, ctx); return;
      case 'amplectobelua': drawAnomalocaris(g, { ...ctx, r: def.size * 0.85 }); return;
    }
    switch (def.tier) {
      case 1: drawPlanktonOrganism(g, ctx); return;
      case 2: drawTrilobite(g, ctx); return;
      case 3: drawHuntingWorm(g, ctx); return;
      case 4: drawAnomalocaris(g, ctx); return;
    }
  }


  // ------------------------------------------------------------------
  // Kill handler — populations + XP + biomass + entity cleanup.
  // NPC kills come via runPredation; player kills come from the
  // projectile-hit branch below.
  // ------------------------------------------------------------------
  private onKill(p: { victimId: number; victimTier: number; victimDefId: string; byPlayer: boolean; biomass: number; xp: number; x: number; y: number }): void {
    if (p.byPlayer) this.tutorial?.report({ kind: 'eat', defId: p.victimDefId });
    const w = this.ctx.world;
    const tier = p.victimTier as CreatureTier;
    // Per-tier juice ramp: bigger creatures hit harder.
    const tierJuice = {
      1: { particles: 6,  spread: 35, shake: 0,   stop: 0,    text: 0 },
      2: { particles: 14, spread: 70, shake: 3,   stop: 0.03, text: 1 },
      3: { particles: 26, spread: 120, shake: 7,  stop: 0.06, text: 1 },
      4: { particles: 80, spread: 260, shake: 26, stop: 0.30, text: 1 },
    } as const;
    const j = tierJuice[tier];
    // Line combo: eat a plankton line member-by-member, in order, within
    // the window. Read the victim's line slot before it is destroyed.
    let lineMult = 1;
    let lineText: string | null = null;
    if (p.byPlayer) {
      const victim = w.get(p.victimId, CreatureComp);
      if (victim?.lineId !== undefined && victim.lineIdx !== undefined) {
        const idx = victim.lineIdx;
        const step = idx - this.lineComboLastIdx;
        const inWindow = this.elapsed - this.lineComboAt < LINE_COMBO_WINDOW;
        const consecutive = victim.lineId === this.lineComboId && Math.abs(step) === 1
          && (this.lineComboDir === 0 || step === this.lineComboDir);
        if (inWindow && consecutive) {
          this.lineCombo += 1;
          this.lineComboDir = step;
        } else {
          this.lineCombo = 1;
          this.lineComboDir = 0;
        }
        this.lineComboId = victim.lineId;
        this.lineComboLastIdx = idx;
        this.lineComboAt = this.elapsed;
        if (this.lineCombo >= 2) {
          lineMult = 1 + LINE_BONUS_PER_STEP * (this.lineCombo - 1);
          lineText = `LINE ×${this.lineCombo}`;
        }
        if (this.lineCombo >= LINE_LEN) {
          // Whole line, in order: a burst of frenzy.
          lineText = 'PERFECT LINE';
          bus.emit('cambrian:perfectLine', undefined);
          this.frenzy += 2;
          this.frenzyT = FRENZY_WINDOW;
          bus.emit('audio:cue', { id: 'bloomCleared', volume: 0.6 });
          bus.emit('fx:particles', { x: p.x, y: p.y, count: 24, color: 0xc4faf0, spread: 110 });
          this.lineCombo = 0;
        }
      }
    }
    // Find + destroy the victim graphics.
    let creatureColor = 0xff5050;
    for (const child of this.creatureLayer.children) {
      if ((child as any).__entity === p.victimId) {
        const c = w.get(p.victimId, CreatureComp);
        if (c) creatureColor = c.color;
        this.creatureLayer.removeChild(child);
        this.reap.push(child);
        break;
      }
    }
    if (w.exists(p.victimId)) w.destroyEntity(p.victimId);
    this.ecosystem.recordRemoval(tier);
    // Juice — fire regardless of who killed (player or NPC predation):
    // creature-on-creature kills should also feel alive on screen.
    if (j.particles > 0) {
      bus.emit('fx:particles', { x: p.x, y: p.y, count: j.particles, color: creatureColor, spread: j.spread });
    }
    if (p.byPlayer) {
      // Frenzy: chain the kill. Multiplier is read *after* stacking so the
      // kill that opens the chain already pays a little extra.
      this.frenzy += 1;
      this.frenzyT = FRENZY_WINDOW;
      bus.emit('cambrian:frenzy', { count: this.frenzy });
      const frenzyMult = this.frenzyBiomassMult();
      if (this.frenzy >= FRENZY_MILESTONE_EVERY && this.frenzy % FRENZY_MILESTONE_EVERY === 0) {
        bus.emit('stage:goalBanner', {
          title: `FRENZY ×${this.frenzy}`,
          subtitle: `${frenzyMult.toFixed(1)}× biomass · faster lunges`,
          durationSec: 1.6,
        });
        bus.emit('audio:cue', { id: 'milestone', volume: 0.5 });
        bus.emit('fx:flash', { color: 0xffd070, alpha: 0.18, duration: 0.3 });
      }
      // One floating label per kill (each is a rasterised Text — several
      // per kill during a chain was the stutter). Plain plankton kills
      // with no chain going get none.
      {
        const shown = Math.round(p.biomass * this.passives.biomassMult * frenzyMult * lineMult);
        const parts: string[] = [];
        if (shown > 0 && (tier >= 2 || this.frenzy >= 2 || lineText)) parts.push(`+${shown.toLocaleString()}`);
        if (this.frenzy >= 2) parts.push(`×${this.frenzy}`);
        if (lineText) parts.push(lineText);
        if (parts.length) {
          bus.emit('fx:floatingText', { x: p.x, y: p.y - 30, text: parts.join('  '), color: lineText ? 0xc4faf0 : 0xffe080 });
        }
      }
      if (p.biomass > 0) {
        const granted = p.biomass * this.passives.biomassMult * frenzyMult * lineMult;
        // Spawn a biomass orb that magnets toward the player instead of
        // adding the value silently. (Falls back to instant if orb system
        // not yet present — implemented in J3.)
        this.spawnBiomassOrb(p.x, p.y, granted);
      }
      if (j.shake > 0) bus.emit('fx:screenShake', { intensity: j.shake, duration: 0.35 });
      if (j.stop > 0) this.hitStopT = j.stop;
      // Apex kill = thunderclap. Slow-mo + flash + giant burst.
      if (tier === 4) {
        bus.emit('fx:timeSlow', { factor: 0.25, duration: 1.3 });
        bus.emit('fx:flash', { color: 0xff80a0, alpha: 0.45, duration: 0.4 });
        bus.emit('audio:cue', { id: 'milestone', volume: 0.7 });
      } else {
        bus.emit('audio:pop', { pitch: 0.7 + (4 - tier) * 0.15, volume: 0.18 + tier * 0.06 });
      }
    }
  }

  /** Biomass orbs — VS-style loot pulled toward the player with magnetism.
   *  Each orb has a tiny scatter velocity at spawn, drifts briefly, then
   *  homes in once the player is within MAGNET_R. On contact, awards
   *  biomass and pops a satisfying audio cue. */
  private biomassOrbs: Array<{
    g: Graphics; x: number; y: number; vx: number; vy: number;
    value: number; age: number; color: number;
  }> = [];

  private spawnBiomassOrb(x: number, y: number, value: number): void {
    // Big kills shed multiple smaller orbs so the screen erupts with loot —
    // small kills get one orb. Caps so the apex doesn't spawn 50 orbs.
    const orbCount = Math.min(8, Math.max(1, Math.floor(value / 12)));
    const perOrb = value / orbCount;
    for (let i = 0; i < orbCount; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 60 + Math.random() * 140;
      const g = new Graphics();
      const color = perOrb >= 40 ? 0xffd070 : perOrb >= 12 ? 0x80ffd0 : 0x60c0ff;
      this.drawOrb(g, color, Math.min(8, 3 + Math.log2(perOrb + 1) * 1.2));
      g.position.set(x, y);
      this.projectileLayer.addChild(g);
      this.biomassOrbs.push({
        g, x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        value: perOrb,
        age: 0,
        color,
      });
    }
  }

  private drawOrb(g: Graphics, color: number, size: number): void {
    g.clear();
    // Outer glow halo
    g.circle(0, 0, size * 2.4).fill({ color, alpha: 0.18 });
    g.circle(0, 0, size * 1.6).fill({ color, alpha: 0.35 });
    // Inner orb
    g.circle(0, 0, size).fill({ color, alpha: 0.95 });
    g.circle(-size * 0.3, -size * 0.3, size * 0.35).fill({ color: 0xffffff, alpha: 0.6 });
  }

  private tickOrbs(dt: number): void {
    if (this.biomassOrbs.length === 0) return;
    const MAGNET_R = 380;
    const PICKUP_R = this.playerRadius + 8;
    const remaining: typeof this.biomassOrbs = [];
    for (const orb of this.biomassOrbs) {
      orb.age += dt;
      // Drag — initial scatter velocity dies off in ~0.4s.
      orb.vx *= Math.exp(-dt * 4.0);
      orb.vy *= Math.exp(-dt * 4.0);
      const dx = this.px - orb.x;
      const dy = this.py - orb.y;
      const dist = Math.hypot(dx, dy);
      if (dist < PICKUP_R) {
        // Pickup! Award biomass + tiny audio pop + brief sparkle.
        player.addBiomass(orb.value);
        bus.emit('audio:pop', { pitch: 1.2 + Math.random() * 0.4, volume: 0.18 });
        bus.emit('fx:particles', { x: orb.x, y: orb.y, count: 4, color: orb.color, spread: 24 });
        orb.g.parent?.removeChild(orb.g);
        this.reap.push(orb.g);
        continue;
      }
      // Magnetism: linear pull within MAGNET_R, accelerating as it nears.
      if (dist < MAGNET_R || orb.age > 0.4) {
        const pull = 1200 * Math.max(0.4, 1 - dist / MAGNET_R) * dt;
        orb.vx += (dx / Math.max(1, dist)) * pull;
        orb.vy += (dy / Math.max(1, dist)) * pull;
      }
      // Soft speed cap so a fast magnet doesn't overshoot the player.
      const sp = Math.hypot(orb.vx, orb.vy);
      const MAX = 720;
      if (sp > MAX) { orb.vx *= MAX / sp; orb.vy *= MAX / sp; }
      orb.x += orb.vx * dt;
      orb.y += orb.vy * dt;
      orb.g.position.set(orb.x, orb.y);
      // Subtle bob/pulse on the orb itself so it reads as "alive".
      const pulse = 1 + Math.sin(orb.age * 8) * 0.10;
      orb.g.scale.set(pulse);
      remaining.push(orb);
    }
    this.biomassOrbs = remaining;
  }

  // ------------------------------------------------------------------
  // Main update loop. M1 keeps it lean: player movement, camera, ticks.
  // ------------------------------------------------------------------
  /** Hit-stop: when > 0, update() short-circuits gameplay (creatures freeze,
   *  projectiles stop) for a tiny window after a satisfying hit. Decays in
   *  real time even while gameplay is paused. */
  private hitStopT = 0;

  /** Wave pressure clock — schedules surges of off-screen spawns. Each wave
   *  shoves a cluster of tier-2 + tier-3 creatures in from a screen edge,
   *  growing in size as waveCount climbs. Telegraphed by a banner so the
   *  player can brace. */
  private waveNextAt = 75;
  private waveCount = 0;

  update(dt: number, _elapsed: number): void {
    this.flushReap();
    if (this.dead) return;
    // Opening cinematic: world frozen, background keeps breathing.
    if (this.intro) {
      this.intro.update(dt);
      this.causticMesh?.update(dt, this.px, this.py);
      this.tickKelp(dt);
      if (this.godrayFilter) this.godrayFilter.time += dt * 0.4;
      this.particles?.update(dt, this.ctx.world);
      return;
    }
    this.tickPendingFlick();
    // Drain any pending level-ups first — opens the modal and pauses.
    if (!this.paused && this.pendingLevelUps > 0) {
      this.pendingLevelUps -= 1;
      this.openLevelUpModal();
    }
    if (!this.paused && this.completePending) {
      this.completePending = false;
      this.openLevelComplete();
    }
    if (this.paused) return;
    this.tutorial?.tick(dt);
    this.drawTutorialMark();
    // Hit-stop: brief gameplay freeze on big kills. Background animation
    // and HUD continue to tick — only the world simulation pauses.
    if (this.hitStopT > 0) {
      this.hitStopT -= dt;
      // Still animate the bg / kelp / godray so the screen doesn't feel frozen.
      this.causticMesh?.update(dt, this.px, this.py);
      this.tickKelp(dt);
      if (this.godrayFilter) this.godrayFilter.time += dt * 0.4;
      return;
    }
    this.elapsed += dt;
    // Scoreboard/stage clock lives in the save so a reload keeps it.
    saveManager.getRun().scoreTime += dt;
    // Live-state snapshot once a second (autosave writes it to storage).
    this.saveLiveAcc += dt;
    if (this.saveLiveAcc >= 1) { this.saveLiveAcc = 0; this.saveLive(); }

    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;

    // Lunge / ATP / frenzy clocks.
    if (this.lungeCooldownT > 0) this.lungeCooldownT = Math.max(0, this.lungeCooldownT - dt);
    if (this.flickCooldownT > 0) this.flickCooldownT = Math.max(0, this.flickCooldownT - dt);
    this.tickSilt(dt);
    if (this.atpRegenDelayT > 0) this.atpRegenDelayT -= dt;
    else if (this.playerAtp < this.atpMax) this.playerAtp = Math.min(this.atpMax, this.playerAtp + ATP_REGEN * this.passives.atpRegenMult * dt);
    if (this.frenzyT > 0) {
      this.frenzyT -= dt;
      if (this.frenzyT <= 0) this.breakFrenzy();
    }

    // Player movement — cursor steering with momentum. Speed scales by
    // accumulated evader passives (moveSpeedMult is additive to 1.0) and
    // the frenzy chain. During a lunge the burst overrides steering.
    const speed = PLAYER_BASE_SPEED * (1 + this.passives.moveSpeedMult + this.frenzySpeedBonus());
    const wmx = this.px + (this.mouseX - sw / 2) / this.worldZoom;
    const wmy = this.py + (this.mouseY - sh / 2) / this.worldZoom;
    const dx = wmx - this.px;
    const dy = wmy - this.py;
    const d = Math.hypot(dx, dy);
    if (this.flickT > 0) {
      this.flickT -= dt;
      this.pvx = this.flickDirX * FLICK_SPEED;
      this.pvy = this.flickDirY * FLICK_SPEED;
    } else if (this.lungeT > 0) {
      this.lungeT -= dt;
      this.pvx = this.lungeDirX * LUNGE_SPEED * this.passives.lungeSpeedMult;
      this.pvy = this.lungeDirY * LUNGE_SPEED * this.passives.lungeSpeedMult;
      this.facing = Math.atan2(this.lungeDirY, this.lungeDirX);
    } else if (d > 1) {
      this.facing = Math.atan2(dy, dx);
      const targetVx = (dx / d) * speed;
      const targetVy = (dy / d) * speed;
      const lerp = 1 - Math.exp(-dt * 4.5);
      this.pvx += (targetVx - this.pvx) * lerp;
      this.pvy += (targetVy - this.pvy) * lerp;
    } else {
      this.pvx *= Math.exp(-dt * 4);
      this.pvy *= Math.exp(-dt * 4);
    }
    this.px += this.pvx * dt;
    this.py += this.pvy * dt;
    // Clamp to world.
    if (this.px < PLAYER_BOUND_PAD) { this.px = PLAYER_BOUND_PAD; this.pvx = 0; }
    if (this.py < PLAYER_BOUND_PAD) { this.py = PLAYER_BOUND_PAD; this.pvy = 0; }
    if (this.px > this.worldW - PLAYER_BOUND_PAD) { this.px = this.worldW - PLAYER_BOUND_PAD; this.pvx = 0; }
    if (this.py > this.worldH - PLAYER_BOUND_PAD) { this.py = this.worldH - PLAYER_BOUND_PAD; this.pvy = 0; }

    // Smooth body rotation toward the cursor-derived `facing` via shortest
    // arc (wrap to [-π, π]). Eased lerp + an angular-velocity cap so even a
    // 180° flick produces a banked turn instead of a snap.
    {
      let delta = this.facing - this.bodyFacing;
      delta = Math.atan2(Math.sin(delta), Math.cos(delta));
      const turnLerp = 1 - Math.exp(-dt * 5.5);
      let step = delta * turnLerp;
      const maxStep = Math.PI * 1.4 * dt; // ≤ 252°/sec angular velocity
      if (step >  maxStep) step =  maxStep;
      if (step < -maxStep) step = -maxStep;
      this.bodyFacing += step;
      // Damp the swim wag while the body is reorienting — eased so the wag
      // doesn't pop back the moment the turn finishes.
      const targetTurn = Math.min(1, Math.abs(delta) / 0.35);
      this.turnIntensity += (targetTurn - this.turnIntensity) * (1 - Math.exp(-dt * 9));
    }

    // Sync ECS position component + transform both player Graphics
    // (halo + body) to the world position and current smoothed facing.
    const pos = this.ctx.world.get(this.playerEntity, Position);
    if (pos) { pos.x = this.px; pos.y = this.py; }
    this.playerGfx.position.set(this.px, this.py);
    this.playerGfx.rotation = this.bodyFacing;
    // Lunge: stretch along the body axis (local X, before rotation) and
    // narrow, easing out over the burst so the bite reads as a snap.
    const lungeStretch = this.lungeT > 0 ? 0.22 * (this.lungeT / LUNGE_DURATION) : 0;
    this.playerGfx.scale.set(1 + lungeStretch, 1 - lungeStretch * 0.6);
    this.playerHaloGfx.position.set(this.px, this.py);
    this.playerHaloGfx.rotation = this.bodyFacing;
    if (this.playerSprite) {
      this.playerSprite.position.set(this.px, this.py);
      // The silhouette is drawn head-right (+X), same as facing=0.
      // Yaw wag = tail swish: amplitude & frequency grow with current speed
      // so it eases to a gentle hover when idle and beats hard at full sprint.
      const maxSpeed = PLAYER_BASE_SPEED * (1 + this.passives.moveSpeedMult);
      const speedFrac = Math.min(1, Math.hypot(this.pvx, this.pvy) / Math.max(1, maxSpeed));
      const wagFreq = 2.5 + speedFrac * 4.5;   // ~0.4 Hz idle → ~1.1 Hz sprint
      const wagAmp  = (0.05 + speedFrac * 0.14) * (1 - this.turnIntensity);
      const wag = Math.sin(this.elapsed * wagFreq) * wagAmp;
      this.playerSprite.rotation = this.bodyFacing + wag;
    }

    // Tick systems.
    this.ecosystem.tick(dt);
    this.ecosystem.runPredation(this.ctx.world, dt);
    this.tickParts(dt);
    // Redraw player composite — body breathe, fin flutter, glyph pulses.
    const moveMag = Math.hypot(this.pvx, this.pvy);
    this.drawPlayer(this.kelpT ?? 0, moveMag);

    // Apex predator spawn — once, when the timer crosses the configured range.
    if (this.apexEntity === 0 && (this.growthIdx >= APEX_GROWTH || this.apexSpawnAt === 0)) {
      this.spawnApex();
    }
    // Wave pressure — telegraphed surge of off-screen creatures.
    if (this.elapsed >= this.waveNextAt) {
      this.fireWave();
      this.waveNextAt = this.elapsed + 60 + Math.random() * 30; // next wave in 60-90s
    }
    // Telegraph a wave 4s before it lands.
    const tilNext = this.waveNextAt - this.elapsed;
    if (tilNext > 0 && tilNext < 4.0 && !(this as any)._wavebanner) {
      (this as any)._wavebanner = true;
      bus.emit('stage:goalBanner', {
        title: this.waveCount === 0 ? 'INCOMING SWARM' : `WAVE ${this.waveCount + 1}`,
        subtitle: 'Tier 2 + 3 surge inbound — brace.',
        durationSec: 3.5,
      });
      bus.emit('audio:cue', { id: 'event', volume: 0.55 });
    }
    if (tilNext > 4.0 && (this as any)._wavebanner) (this as any)._wavebanner = false;
    // No duration timer: the stage is won by biomass (see onBiomassChanged),
    // like L1/L2. cfg.duration_seconds is kept in the JSON for the apex
    // schedule only.

    // Creature movement + player contact + projectile collision.
    this.tickCreatures(dt);
    this.tickLungeBites();
    this.tickProjectiles(dt);

    // Particle system + floating text — drives all the burst FX we emit
    // via bus.emit('fx:particles' / 'fx:floatingText').
    this.particles?.update(dt, this.ctx.world);
    // Biomass orbs (VS-style pickup magnetism).
    this.tickOrbs(dt);

    // Off-screen apex arrow.
    this.updateApexArrow();

    // Caustic shader + kelp sway + godray time.
    this.causticMesh?.update(dt, this.px, this.py);
    this.tickKelp(dt);
    if (this.godrayFilter) this.godrayFilter.time += dt * 0.4;

    // Passive HP regen — base 1/s + blood gills.
    if (this.invulnT === 0 && this.playerHp < this.playerHpMax) {
      const regen = 1.0 + this.passives.hpRegen;
      this.playerHp = Math.min(this.playerHpMax, this.playerHp + regen * dt);
    }

    // Camera deadzone follow.
    const halfW = sw * CAM_DEADZONE_FRAC_X * 0.5;
    const halfH = sh * CAM_DEADZONE_FRAC_Y * 0.5;
    const dxc = this.px - this.camX;
    const dyc = this.py - this.camY;
    if (dxc > halfW) this.camX = this.px - halfW;
    else if (dxc < -halfW) this.camX = this.px + halfW;
    if (dyc > halfH) this.camY = this.py - halfH;
    else if (dyc < -halfH) this.camY = this.py + halfH;
    this.updateCamera();

    // HUD ticking.
    this.updateHud();
    this.updateGoalBanner();
  }

  private tickCreatures(dt: number): void {
    const w = this.ctx.world;
    const deadIds: number[] = [];

    // Decrement the player's invuln window once per frame.
    if (this.invulnT > 0) this.invulnT = Math.max(0, this.invulnT - dt);

    const maxTier = this.maxBiteTier;
    // How many hunters are already on the player this frame, and how many
    // may be at once (2 at hatchling size, +1 per two growth steps).
    let chasingNow = 0;
    for (const e of w.query([CreatureComp])) { const cc = w.get(e, CreatureComp)!; if ((cc.chaseT ?? 0) > 0) chasingNow++; }
    const packCap = 2 + Math.floor(this.growthIdx / 2);

    for (const e of w.query([CreatureComp, Position])) {
      const c = w.get(e, CreatureComp)!;
      const p = w.get(e, Position)!;
      // Where it started this frame — contact is tested along the whole
      // path it moves, so a fast strike can't tunnel through the player.
      const mx0 = p.x, my0 = p.y;

      // --- Threat AI: anything too big to bite hunts the player. ---
      // Idle threats drift; within aggro range they take interest (rate by
      // tier), then pursue at chase speed, wind up a telegraphed strike
      // when close, burst forward, and lose interest after a while, when
      // the player gets far, or when they hit a silt cloud.
      const dxp = this.px - p.x;
      const dyp = this.py - p.y;
      const distP = Math.hypot(dxp, dyp);
      const threat = c.damage > 0 && c.tier > maxTier;
      let speedMult = 1;
      if ((c.cool ?? 0) > 0) c.cool! -= dt;
      if (threat) {
        // Hunters are a real threat from minute one: they notice you from
        // far, commit fast, chase at a speed close to yours (absolute
        // floors, since base swim speeds are slow), keep at it, and strike
        // with a short tell. Shaking one takes distance or a silt cloud.
        // Chase speeds bracket the player's 240 px/s: tier 2 can be
        // outswum with effort, tier 3 keeps pace, the apex is faster. The
        // strike itself (lead + homing burst) is what you have to dodge.
        const hunt = c.tier === 4
          ? { aggro: 1300, rate: 2.0, chase: 300, burst: 660, time: 10, cool: 2.5 }
          : c.tier === 3
            ? { aggro: 850, rate: 1.0, chase: 250, burst: 580, time: 8, cool: 3.0 }
            : { aggro: 560, rate: 0.6, chase: 200, burst: 500, time: 6, cool: 3.5 };
        // Pack cap: only a few hunters commit at once, more as you grow.
        if ((c.chaseT ?? 0) <= 0 && (c.cool ?? 0) <= 0 && distP < hunt.aggro && chasingNow < packCap) {
          if (Math.random() < hunt.rate * dt) { c.chaseT = hunt.time; c.windup = 0; c.burst = 0; chasingNow++; }
        }
        if ((c.chaseT ?? 0) > 0) {
          c.chaseT! -= dt;
          // The ecosystem's prey-hunting and wander AI write c.heading every
          // frame; while hunting the player, our heading wins.
          if (c.huntHeading !== undefined) c.heading = c.huntHeading;
          c.target = 0;
          if (distP > hunt.aggro * 2.0) {
            c.chaseT = 0; c.cool = hunt.cool; c.windup = 0; c.burst = 0; c.huntHeading = undefined;
          } else if ((c.burst ?? 0) > 0) {
            c.burst! -= dt;
            // The strike curves toward you for its first stretch (limited
            // turn rate): swimming straight on won't shake it, a hard cut,
            // a lunge or a flick will. The last stretch flies straight.
            if (c.burst! > HUNT_BURST - 0.28) {
              const want = Math.atan2(dyp + this.pvy * 0.12, dxp + this.pvx * 0.12);
              let d = want - c.heading;
              d = Math.atan2(Math.sin(d), Math.cos(d));
              const maxTurn = 7 * dt;
              c.heading += Math.max(-maxTurn, Math.min(maxTurn, d));
            }
            speedMult = Math.max(6, hunt.burst / Math.max(1, c.speed));
          } else if ((c.windup ?? 0) > 0) {
            // Keep closing while telegraphing — pausing here let the gap
            // grow past what the burst could cover, so strikes fell short.
            c.windup! -= dt;
            const want = Math.atan2(dyp, dxp);
            let d = want - c.heading;
            d = Math.atan2(Math.sin(d), Math.cos(d));
            c.heading += d * Math.min(1, dt * 6);
            speedMult = Math.max(2.6, hunt.chase / Math.max(1, c.speed));
            if (c.windup! <= 0) {
              // Launch with lead: aim where you'll be, not where you are.
              c.burst = HUNT_BURST;
              c.heading = Math.atan2(dyp + this.pvy * 0.22, dxp + this.pvx * 0.22);
            }
          } else {
            const want = Math.atan2(dyp, dxp);
            let d = want - c.heading;
            d = Math.atan2(Math.sin(d), Math.cos(d));
            c.heading += d * Math.min(1, dt * 5);
            speedMult = Math.max(2.6, hunt.chase / Math.max(1, c.speed));
            if (distP < 160 + c.size + this.playerRadius) {
              c.windup = HUNT_WINDUP;
              this.tutorial?.report({ kind: 'threat' });
            }
          }
          if ((c.chaseT ?? 0) > 0) c.huntHeading = c.heading;
          for (const s of this.siltClouds) {
            if (Math.hypot(s.x - p.x, s.y - p.y) < s.r + c.size) {
              c.chaseT = 0; c.cool = 5; c.windup = 0; c.burst = 0; c.huntHeading = undefined;
              c.heading += Math.PI * (0.5 + Math.random() * 0.5);
              break;
            }
          }
        }
      } else if (c.tier <= maxTier && c.tier >= 1) {
        // --- Prey evasion. ---
        // A rushing or lunging player startles prey within reach: it darts
        // sideways-and-away in a quick burst, then settles. A slow approach
        // doesn't (stalking is rewarded). Bigger prey also keeps fleeing.
        if ((c.startleCd ?? 0) > 0) c.startleCd! -= dt;
        const playerSpeed = Math.hypot(this.pvx, this.pvy);
        const rushing = playerSpeed > 150 || this.lungeT > 0;
        const reach = c.tier === 1 ? 150 : 200;
        if (rushing && distP < reach && (c.startleCd ?? 0) <= 0) {
          const ax = -dxp / Math.max(1, distP), ay = -dyp / Math.max(1, distP); // away
          const side = Math.random() < 0.5 ? -1 : 1;
          const sx = -ay * side, sy = ax * side; // perpendicular
          const dartSpeed = c.tier === 1 ? 300 : 260;
          c.dartVx = (ax * 0.6 + sx * 0.8) * dartSpeed;
          c.dartVy = (ay * 0.6 + sy * 0.8) * dartSpeed;
          c.startleCd = c.tier === 1 ? 1.1 : 0.9;
          c.heading = Math.atan2(c.dartVy, c.dartVx);
        }
        if (c.tier >= 2 && distP < 220) {
          const away = Math.atan2(-dyp, -dxp);
          let d = away - c.heading;
          d = Math.atan2(Math.sin(d), Math.cos(d));
          c.heading += d * Math.min(1, dt * 5);
          speedMult = 2.2;
        }
      }
      // Dart velocity decays fast (a flick, not a run).
      if (c.dartVx || c.dartVy) {
        p.x += (c.dartVx ?? 0) * dt;
        p.y += (c.dartVy ?? 0) * dt;
        const k = Math.exp(-dt * 6);
        c.dartVx = (c.dartVx ?? 0) * k;
        c.dartVy = (c.dartVy ?? 0) * k;
        if (Math.abs(c.dartVx) + Math.abs(c.dartVy) < 4) { c.dartVx = 0; c.dartVy = 0; }
      }

      // Plankton lines hold straight for a while, then each member starts
      // to wander on its own phase so the line bends and breaks up.
      if (c.lineId !== undefined && this.elapsed - (c.lineT ?? 0) > LINE_HOLD_SEC) {
        c.heading += Math.sin(this.elapsed * 1.3 + (c.phase ?? 0) * 2) * 1.4 * dt;
      }
      const sp = c.speed * speedMult;
      p.x += Math.cos(c.heading) * sp * dt;
      p.y += Math.sin(c.heading) * sp * dt;
      // Smooth the drawn heading toward the AI heading (shortest arc) so
      // wander/flee/chase snaps read as turns, not teleports. Faster turn
      // rate when darting or striking so the body still leads the motion.
      {
        const cur = c.drawHeading ?? c.heading;
        let d = c.heading - cur;
        d = Math.atan2(Math.sin(d), Math.cos(d));
        const quick = (c.burst ?? 0) > 0 || (c.dartVx ?? 0) !== 0 || (c.dartVy ?? 0) !== 0;
        c.drawHeading = cur + d * Math.min(1, dt * (quick ? 18 : 7));
      }

      // Bounce off world boundaries.
      if (p.x < 80 || p.x > this.worldW - 80) c.heading = Math.PI - c.heading;
      if (p.y < 80 || p.y > this.worldH - 80) c.heading = -c.heading;
      p.x = Math.max(60, Math.min(this.worldW - 60, p.x));
      p.y = Math.max(60, Math.min(this.worldH - 60, p.y));

      // Player-vs-creature contact damage. Anything a full tier below what
      // you can bite through is harmless prey; your top prey tier still nips
      // back. Player gets brief i-frames after, and is untouchable mid-lunge
      // — the lunge is the attack, not a risk.
      // Threats only hurt while striking (the wind-up is the tell — flick
      // away). Your top prey tier still nips on contact.
      const canHurt = this.tutorial?.phase !== 'feed' && (c.tier > maxTier ? (c.burst ?? 0) > 0 : c.tier === maxTier);
      // A striking hunter's jaws reach a little past its body.
      const rr = this.playerRadius + c.size + ((c.burst ?? 0) > 0 ? 12 : 0);
      // Swept overlap: closest point on this frame's movement segment.
      const sx = p.x - mx0, sy = p.y - my0;
      const seg2 = sx * sx + sy * sy;
      const tt = seg2 > 0 ? Math.max(0, Math.min(1, ((this.px - mx0) * sx + (this.py - my0) * sy) / seg2)) : 0;
      const ox = this.px - (mx0 + sx * tt), oy = this.py - (my0 + sy * tt);
      const overlap = ox * ox + oy * oy < rr * rr;
      if (overlap && this.invulnT === 0 && this.lungeT <= 0 && this.flickT <= 0 && !this.dead && c.damage > 0 && canHurt) {
        this.applyPlayerDamage(c.damage, p.x, p.y, e);
        // A landed strike ends the chase: the hunter peels off for a
        // breather instead of re-biting the moment your grace ends.
        if (threat) { c.chaseT = 0; c.burst = 0; c.windup = 0; c.huntHeading = undefined; c.cool = 2.5; }
      } else if (overlap && threat) {
        // Bumping a hunter that isn't striking just shoves you off it.
        const ddx = this.px - p.x, ddy = this.py - p.y;
        const d = Math.max(1, Math.hypot(ddx, ddy));
        this.px += (ddx / d) * Math.max(0, rr - d) * 0.5;
        this.py += (ddy / d) * Math.max(0, rr - d) * 0.5;
      }
    }
    // Sync graphics positions AND redraw with current time-driven pose.
    // Viewport culling: drawCreature is the most expensive per-frame work
    // in the stage (procedural anatomy = ~30 vector ops per creature). At
    // ~190 creatures spread over a 4000×4000 world, the vast majority are
    // offscreen. Skip their redraw + sprite tweens entirely — they keep
    // moving (AI ran above), just don't repaint until they're visible.
    const tNow = (this.kelpT ?? 0); // shared time clock — kelpT ticks every frame
    const cullHalfW = this.ctx.app.screen.width  * 0.5 / this.worldZoom + 220;
    const cullHalfH = this.ctx.app.screen.height * 0.5 / this.worldZoom + 220;
    for (const child of this.creatureLayer.children) {
      const id = (child as any).__entity as number | undefined;
      if (!id) continue;
      const p = w.get(id, Position);
      if (!p) { deadIds.push(id); continue; }
      child.position.set(p.x, p.y);
      const c = w.get(id, CreatureComp);
      if (!c) continue;
      const onscreen = Math.abs(p.x - this.camX) < cullHalfW
                    && Math.abs(p.y - this.camY) < cullHalfH;
      child.visible = onscreen;
      if (!onscreen) continue;
      const def = this.creatures.find((d) => d.id === c.defId);
      if (!def) continue;
      // Re-draw the procedural body (animated anatomy).
      const body = (child as any).__body as Graphics | undefined;
      if (body) {
        this.drawCreature(body, def, tNow, c.phase ?? 0, c.drawHeading ?? c.heading);
        // Threat cue only when it matters: the hunter is on you, or close
        // enough that it could be. A ring on every oversized creature on
        // screen was noise.
        if (c.damage > 0 && c.tier > maxTier) {
          const hunting = (c.chaseT ?? 0) > 0;
          const near = Math.hypot(p.x - this.px, p.y - this.py) < 380;
          if (hunting || near) {
            drawThreatRing(body, c.size / (c.scale ?? 1), tNow, hunting, (c.windup ?? 0) > 0 ? c.windup! / HUNT_WINDUP : 0);
          }
        }
      }
      // Rotate + breathe the silhouette sprite if present. The silhouette
      // is rotated to face heading + a gentle scale wobble for life.
      const sprite = (child as any).__sprite as Sprite | undefined;
      const baseScale = (child as any).__spriteBaseScale as number | undefined;
      if (sprite && baseScale !== undefined) {
        sprite.rotation = c.heading;
        const breathe = 1.0 + Math.sin(tNow * 2.0 + (c.phase ?? 0)) * 0.06;
        sprite.scale.set(baseScale * breathe);
      }
      // V-wake puffs for tier-3/4 predators (only when onscreen — invisible
      // puffs aren't worth the per-frame integration cost).
      if (c.tier >= 3) {
        const interval = c.tier === 4 ? 0.10 : 0.15;
        const acc = ((child as any).__wakeAcc ?? 0) + dt;
        if (acc >= interval) {
          (child as any).__wakeAcc = 0;
          this.spawnVWake(p.x, p.y, c.heading, (c.size ?? 16) * 0.9, c.tier === 4 ? 1.5 : 1.0);
        } else {
          (child as any).__wakeAcc = acc;
        }
      }
    }
    // Player wake: V-wake + bow splash + tail-beat splash, all gated by speed.
    {
      const maxSpeed = PLAYER_BASE_SPEED * (1 + this.passives.moveSpeedMult);
      const speedFrac = Math.min(1, Math.hypot(this.pvx, this.pvy) / Math.max(1, maxSpeed));
      if (speedFrac > 0.15) {
        const interval = 0.07;
        this.wakePlayerAcc += dt;
        while (this.wakePlayerAcc >= interval) {
          this.wakePlayerAcc -= interval;
          this.spawnVWake(this.px, this.py, this.bodyFacing, this.playerRadius, this.sizeScale);
          // Bow splash: tiny bright spritz at the head while sprinting.
          if (speedFrac > 0.5) this.spawnBowSplash(this.px, this.py, this.bodyFacing, this.playerRadius, speedFrac);
        }
        // Tail-beat splash: fire when the wag clock crosses zero (stroke
        // peak), so the splash punctuates each tail stroke instead of
        // ticking on a fixed timer.
        const wagFreq = 2.5 + speedFrac * 4.5;
        const wagNow = Math.sin(this.elapsed * wagFreq);
        const signNow = wagNow >= 0 ? 1 : -1;
        if (signNow !== this.wakeLastWagSign && this.wakeLastWagSign !== 0) {
          this.spawnTailBeatSplash(this.px, this.py, this.bodyFacing, this.playerRadius, speedFrac, signNow);
        }
        this.wakeLastWagSign = signNow;
      } else {
        this.wakePlayerAcc = 0;
        this.wakeLastWagSign = 0;
      }
    }
    this.tickWakeFx(dt);
    this.drawLines();
  }

  /** Thread through each intact line's members; fades as the hold expires. */
  private drawLines(): void {
    const w = this.ctx.world;
    const g = this.lineGfx;
    g.clear();
    for (const [id, line] of this.lines) {
      line.ids = line.ids.filter((e) => w.exists(e));
      // Drop lines that are fully eaten — but never the one still filling.
      if (line.ids.length === 0 && id !== this.currentLine?.id) { this.lines.delete(id); continue; }
      if (line.ids.length < 2) continue;
      const age = this.elapsed - line.bornAt;
      const alpha = age < LINE_HOLD_SEC ? 0.22 : Math.max(0, 0.22 * (1 - (age - LINE_HOLD_SEC) / 3));
      if (alpha <= 0) continue;
      // Join only neighbours still close together — once members scatter,
      // a thread stretched across the screen reads as a web, not a line.
      let prev: { x: number; y: number } | null = null;
      let drew = false;
      for (const e of line.ids) {
        const p = w.get(e, Position)!;
        if (prev && Math.hypot(p.x - prev.x, p.y - prev.y) < LINE_SPACING * 2.2) {
          g.moveTo(prev.x, prev.y).lineTo(p.x, p.y);
          drew = true;
        }
        prev = p;
      }
      if (drew) g.stroke({ color: 0xc4faf0, width: 1.2, alpha });
    }
  }

  /** A pair of thin surface ripples expanding from the rear flanks —
   *  reads as the V-wake of a swimmer disturbing the water surface. */
  private spawnVWake(cx: number, cy: number, heading: number, radius: number, scale: number): void {
    const cosH = Math.cos(heading), sinH = Math.sin(heading);
    const back = -radius * 0.6;
    const side = radius * 0.55;
    const drift = 14 * scale; // gentle outward drift; ripples mostly grow in place
    for (const s of [-1, 1]) {
      const x = cx + cosH * back + (-sinH) * side * s;
      const y = cy + sinH * back + ( cosH) * side * s;
      const vx = (-sinH) * drift * s + (-cosH) * drift * 0.2;
      const vy = ( cosH) * drift * s + (-sinH) * drift * 0.2;
      this.wakePuffs.push({
        kind: 'ripple',
        x, y, vx, vy,
        age: 0, life: 1.0 + Math.random() * 0.3,
        r0: (4 + Math.random() * 1.5) * scale,
        r1: (22 + Math.random() * 8) * scale,
        alpha: 0.28, color: 0xbfe6ff,
        thickness: 1.4 * scale,
      });
    }
    this.capPuffs();
  }

  /** Tiny bright glint droplets at the head — the only spot a bit of real
   *  spray reads, since the body is parting the surface there. */
  private spawnBowSplash(cx: number, cy: number, heading: number, radius: number, speedFrac: number): void {
    const cosH = Math.cos(heading), sinH = Math.sin(heading);
    const front = radius * 0.85;
    for (let i = 0; i < 2; i++) {
      const spread = (Math.random() - 0.5) * 0.7;
      const x = cx + cosH * front + (-sinH) * spread * radius * 0.35;
      const y = cy + sinH * front + ( cosH) * spread * radius * 0.35;
      const speed = 40 + speedFrac * 50 + Math.random() * 25;
      const dirAngle = heading + spread * 0.5;
      this.wakePuffs.push({
        kind: 'glint',
        x, y,
        vx: Math.cos(dirAngle) * speed,
        vy: Math.sin(dirAngle) * speed,
        age: 0, life: 0.22 + Math.random() * 0.12,
        r0: 1.2 + Math.random() * 0.5,
        r1: 2.2 + Math.random() * 1.2,
        alpha: 0.55, color: 0xffffff,
      });
    }
    this.capPuffs();
  }

  /** Tail-beat ripple — a single circular ripple expanding from where the
   *  tail tip just whipped through the water. Pure ring, no spray. */
  private spawnTailBeatSplash(cx: number, cy: number, heading: number, radius: number, speedFrac: number, side: number): void {
    const cosH = Math.cos(heading), sinH = Math.sin(heading);
    const back = -radius * 1.2;
    const sideOff = radius * 0.45 * side;
    const x = cx + cosH * back + (-sinH) * sideOff;
    const y = cy + sinH * back + ( cosH) * sideOff;
    this.wakePuffs.push({
      kind: 'ripple',
      x, y,
      vx: 0, vy: 0,
      age: 0, life: 0.55 + Math.random() * 0.15,
      r0: 3, r1: 22 + speedFrac * 10,
      alpha: 0.32, color: 0xdaf2ff,
      thickness: 1.6,
    });
    this.capPuffs();
  }

  private capPuffs(): void {
    if (this.wakePuffs.length > 800) this.wakePuffs.splice(0, this.wakePuffs.length - 800);
  }

  /** Integrate, age, and redraw all wake puffs in one shared Graphics. */
  private tickWakeFx(dt: number): void {
    this.wakeGfx.clear();
    const drag = Math.exp(-dt * 2.5); // water friction
    for (let i = this.wakePuffs.length - 1; i >= 0; i--) {
      const p = this.wakePuffs[i];
      p.age += dt;
      if (p.age >= p.life) { this.wakePuffs.splice(i, 1); continue; }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= drag;
      p.vy *= drag;
      const t = p.age / p.life;
      const r = p.r0 + (p.r1 - p.r0) * t;
      const a = p.alpha * (1 - t) * (1 - t);
      if (p.kind === 'ripple') {
        // Thin spreading ring — water surface displacement, not foam.
        this.wakeGfx.circle(p.x, p.y, r)
          .stroke({ color: p.color, alpha: a, width: p.thickness ?? 1.4 });
      } else {
        this.wakeGfx.circle(p.x, p.y, r).fill({ color: p.color, alpha: a });
      }
    }
  }

  /** Show the OS cursor whenever the stage is paused (evolution pick or
   *  user pause); hide it again for play. */
  private userPaused = false;
  private syncCursor(): void {
    document.body.classList.toggle('stage-cursor', this.paused || this.userPaused);
  }

  /** Biomass goal reached: stop play and show the run's stats. */
  private openLevelComplete(): void {
    bus.emit('stage:complete', { stageId: 'cambrian', summary: { time: this.elapsed, biomass: player.biomass } });
    this.paused = true;
    this.syncCursor();
    const next = stageManager.nextOf('cambrian');
    this.levelComplete.open({
      time: this.elapsed,
      biomass: player.biomass,
      killsByTier: this.killsByTier,
      deaths: this.deaths,
      evolutions: this.evolutionsEarned,
      apexSlain: this.apexSlain,
      parts: this.parts,
    }, {
      onNext: next ? () => { stageManager.advance().catch((err) => console.warn('[Cambrian] advance failed', err)); } : null,
      onKeepPlaying: () => { this.paused = false; this.syncCursor(); },
      onMenu: () => {
        const ctx = this.ctx;
        showStartMenu().then(() => {
          const stageId = saveManager.getRun().stageId;
          stageManager.start(stageId, ctx).catch((err) => console.warn('[Cambrian] menu restart failed', err));
        });
      },
    });
  }

  private openLevelUpModal(): void {
    const offers = rollOffers(this.parts);
    if (offers.length === 0) return; // everything maxed — nothing to offer
    this.paused = true;
    this.syncCursor();
    bus.emit('audio:cue', { id: 'milestone', volume: 0.45 });
    this.levelUpModal.open(offers, this.parts, this.growthIdx, (id) => {
      this.paused = false;
      this.syncCursor();
      this.growPart(id);
    });
  }

  /** Animate the kelp field. Cull offscreen instances: kelp is spread over
   *  the 4000×4000 world but the camera only sees a small fraction. The
   *  redraw is procedural (per-segment vector ops), so skipping ~22 of 28
   *  fronds per frame is a meaningful win. */
  private kelpT = 0;
  private tickKelp(dt: number): void {
    this.kelpT += dt;
    const cullHalfW = this.ctx.app.screen.width  * 0.5 / this.worldZoom + 200;
    const cullHalfH = this.ctx.app.screen.height * 0.5 / this.worldZoom + 200;
    for (const inst of this.kelpInstances) {
      const onscreen = Math.abs(inst.baseX - this.camX) < cullHalfW
                    && Math.abs(inst.baseY - this.camY) < cullHalfH;
      inst.g.visible = onscreen;
      if (onscreen) this.drawKelp(inst, this.kelpT);
    }
  }

  /** Off-screen apex marker — a small floating arrow on the screen edge
   *  pointing toward the apex while it's alive but outside the viewport. */
  private updateApexArrow(): void {
    if (this.apexEntity === 0 || !this.ctx.world.exists(this.apexEntity)) {
      if (this.domApexArrow) { this.domApexArrow.remove(); this.domApexArrow = null; }
      return;
    }
    const pos = this.ctx.world.get(this.apexEntity, Position);
    if (!pos) return;
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;
    // Apex world -> screen via worldContainer offset.
    const sx = pos.x * this.worldZoom + this.worldContainer.position.x;
    const sy = pos.y * this.worldZoom + this.worldContainer.position.y;
    const margin = 60;
    const onScreen = sx >= margin && sx <= sw - margin && sy >= margin && sy <= sh - margin;
    if (onScreen) {
      if (this.domApexArrow) { this.domApexArrow.remove(); this.domApexArrow = null; }
      return;
    }
    if (!this.domApexArrow) {
      const el = document.createElement('div');
      el.id = 'cambrian-apex-arrow';
      el.innerHTML = '<div class="ca-tri"></div><div class="ca-lbl">APEX</div>';
      document.body.appendChild(el);
      this.domApexArrow = el;
    }
    // Project the apex direction onto the screen edge with a small inset.
    const cx = sw / 2;
    const cy = sh / 2;
    const dx = sx - cx;
    const dy = sy - cy;
    const angle = Math.atan2(dy, dx);
    const halfW = sw / 2 - 40;
    const halfH = sh / 2 - 40;
    const ax = Math.abs(Math.cos(angle));
    const ay = Math.abs(Math.sin(angle));
    const t = Math.min(halfW / Math.max(0.0001, ax), halfH / Math.max(0.0001, ay));
    const ex = cx + Math.cos(angle) * t;
    const ey = cy + Math.sin(angle) * t;
    this.domApexArrow.style.left = `${ex}px`;
    this.domApexArrow.style.top = `${ey}px`;
    (this.domApexArrow.querySelector('.ca-tri') as HTMLElement).style.transform = `rotate(${angle}rad)`;
  }

  /** Spawn a wave of tier-2 + tier-3 creatures at the edges of the player's
   *  view, all converging inward. Size scales with wave count + biomass. */
  private fireWave(): void {
    this.waveCount += 1;
    const t2Count = 6 + this.waveCount * 3;
    const t3Count = 1 + Math.floor(this.waveCount / 2);
    // Tier-2 swarm — fan out around the player.
    for (let i = 0; i < t2Count; i++) {
      const id = this.ecosystem.spawnAtTier(2);
      if (id === 0) continue;
      const pos = this.ctx.world.get(id, Position);
      if (!pos) continue;
      // Reposition the freshly spawned creature on a ring around the player.
      const a = (i / t2Count) * Math.PI * 2 + Math.random() * 0.3;
      const dist = 700 + Math.random() * 200;
      pos.x = Math.max(80, Math.min(this.worldW - 80, this.px + Math.cos(a) * dist));
      pos.y = Math.max(80, Math.min(this.worldH - 80, this.py + Math.sin(a) * dist));
      const c = this.ctx.world.get(id, CreatureComp);
      if (c) c.heading = Math.atan2(this.py - pos.y, this.px - pos.x);
    }
    // Tier-3 hunters from random directions.
    for (let i = 0; i < t3Count; i++) {
      const id = this.ecosystem.spawnAtTier(3);
      if (id === 0) continue;
      const pos = this.ctx.world.get(id, Position);
      if (!pos) continue;
      const a = Math.random() * Math.PI * 2;
      const dist = 900;
      pos.x = Math.max(80, Math.min(this.worldW - 80, this.px + Math.cos(a) * dist));
      pos.y = Math.max(80, Math.min(this.worldH - 80, this.py + Math.sin(a) * dist));
      const c = this.ctx.world.get(id, CreatureComp);
      if (c) c.heading = Math.atan2(this.py - pos.y, this.px - pos.x);
    }
    // Sting of a screen shake to sell the surge arriving.
    bus.emit('fx:screenShake', { intensity: 4, duration: 0.35 });
    bus.emit('audio:cue', { id: 'event', volume: 0.7 });
  }

  /** Force-spawn the apex predator (called once when the apex timer fires). */
  private spawnApex(): void {
    const apexDef = this.creatures.find((d) => d.tier === 4);
    if (!apexDef) return;
    const id = this.ecosystem.spawnSpecific(apexDef.id);
    if (id > 0) {
      this.apexEntity = id;
      // Mark the creature instance as apex for special handling.
      const c = this.ctx.world.get(id, CreatureComp);
      if (c) c.isApex = true;
      bus.emit('cambrian:apex:spawned', { entityId: id });
      bus.emit('stage:goalBanner', {
        title: 'APEX PREDATOR DETECTED',
        subtitle: 'Anomalocaris has entered the biome.',
        durationSec: 5,
      });
      bus.emit('audio:cue', { id: 'event', volume: 0.7 });
    }
  }

  /** `attacker` = the creature that hit you (thorns strike back at it);
   *  poison passes `blockable = false` so the shield can't eat it. */
  private applyPlayerDamage(amount: number, fromX: number, fromY: number, attacker = 0, blockable = true): void {
    const w = this.ctx.world;
    // Thorn spines hurt (and at L2+ throw back) whatever hit you.
    const thorns = partLevel(this.parts, 'thorns');
    if (thorns > 0 && attacker && w.exists(attacker)) {
      const c = w.get(attacker, CreatureComp);
      const ap = w.get(attacker, Position);
      if (c && ap) {
        this.partFx.thornFlash = 1;
        bus.emit('fx:floatingText', { x: ap.x, y: ap.y - 28, text: `THORNS −${PART_STATS.thornDamage[thorns]}`, color: 0xffa060 });
        bus.emit('fx:particles', { x: ap.x, y: ap.y, count: 10, color: 0xff8a40, spread: 50 });
        const kb = PART_STATS.thornKnockback[thorns];
        if (kb > 0) {
          const kx = ap.x - this.px, ky = ap.y - this.py;
          const kd = Math.hypot(kx, ky) || 1;
          c.dartVx = (kx / kd) * kb;
          c.dartVy = (ky / kd) * kb;
        }
        this.damageCreature(attacker, PART_STATS.thornDamage[thorns]);
      }
    }
    // Chitin shield: a banked charge swallows the whole hit.
    if (blockable && this.partFx.shieldCharges > 0) {
      this.partFx.shieldCharges -= 1;
      this.partFx.shieldFlash = 1;
      if (this.shieldRechargeT <= 0) this.shieldRechargeT = PART_STATS.shieldRecharge[partLevel(this.parts, 'shield')];
      this.invulnT = 1.0;
      bus.emit('fx:floatingText', { x: this.px, y: this.py - 40, text: 'BLOCKED', color: 0xa8d0ff });
      bus.emit('fx:particles', { x: this.px, y: this.py, count: 18, color: 0xbfe0ff, spread: 80 });
      bus.emit('audio:cue', { id: 'pulse', volume: 0.35 });
      const dx = this.px - fromX, dy = this.py - fromY;
      const d = Math.hypot(dx, dy) || 1;
      this.pvx += (dx / d) * 140;
      this.pvy += (dy / d) * 140;
      return;
    }
    const dmg = amount * this.passives.damageMult;
    this.playerHp = Math.max(0, this.playerHp - dmg);
    this.invulnT = 1.0;
    this.breakFrenzy();
    bus.emit('fx:particles', { x: this.px, y: this.py, count: 10, color: 0xff5050, spread: 60 });
    bus.emit('audio:cue', { id: 'damage', volume: 0.3 });
    // Knockback away from the attacker.
    const dx = this.px - fromX;
    const dy = this.py - fromY;
    const d = Math.hypot(dx, dy) || 1;
    this.pvx += (dx / d) * 200;
    this.pvy += (dy / d) * 200;
    if (this.playerHp <= 0 && !this.dead) this.respawnAfterDeath();
  }

  /**
   * Death: biomass drops back to where you last evolved; dying again before
   * you grow drops you a further evolution each time. Parts are always
   * kept. You reform
   * at the centre with full HP/ATP and a grace window, everything nearby
   * shoved away.
   */
  private respawnAfterDeath(): void {
    this.deaths++;
    bus.emit('player:died', undefined);
    // First death: biomass earned since your last evolution is lost. Each
    // further death without growing drops you one more evolution's worth
    // (and a size). Body parts are always kept.
    this.deathStreak++;
    // (After the first death you sit exactly on your level's threshold, so
    // each later one steps down from the size you're at now.)
    const level = Math.max(0, this.deathStreak === 1 ? this.growthIdx : this.growthIdx - 1);
    const checkpoint = level > 0 ? GROWTH_THRESHOLDS[level - 1] : 0;
    const lost = Math.max(0, Math.floor(player.biomass - checkpoint));
    if (lost > 0) player.addBiomass(-lost);
    this.partFx.shieldCharges = PART_STATS.shieldCharges[partLevel(this.parts, 'shield')];
    this.shieldRechargeT = 0;
    this.quillT = 0;
    this.breakFrenzy();
    this.playerHp = this.playerHpMax;
    this.playerAtp = this.atpMax;
    this.invulnT = 3.0;
    this.lungeT = 0;
    this.lungeCooldownT = 0;
    this.pvx = 0; this.pvy = 0;
    this.flickT = 0;
    this.breakFrenzy();
    // Reform at the centre of the sea, like the earlier levels' respawns,
    // with the camera snapped there and every hunter near it pushed far
    // out and its chase dropped, so you get a quiet moment to re-orient.
    this.px = this.worldW / 2;
    this.py = this.worldH / 2;
    this.camX = this.px;
    this.camY = this.py;
    this.updateCamera();
    const w = this.ctx.world;
    for (const e of w.query([CreatureComp, Position])) {
      const c = w.get(e, CreatureComp)!;
      const p = w.get(e, Position)!;
      c.chaseT = 0; c.windup = 0; c.burst = 0; c.huntHeading = undefined; c.cool = 4; c.target = 0;
      if (c.tier < 2) continue;
      const dx = p.x - this.px, dy = p.y - this.py;
      const d = Math.hypot(dx, dy);
      if (d > 900) continue;
      const a = d > 1 ? Math.atan2(dy, dx) : Math.random() * Math.PI * 2;
      const far = 1000 + Math.random() * 400;
      p.x = Math.max(80, Math.min(this.worldW - 80, this.px + Math.cos(a) * far));
      p.y = Math.max(80, Math.min(this.worldH - 80, this.py + Math.sin(a) * far));
      c.heading = a;
    }
    bus.emit('stage:goalBanner', {
      title: 'EATEN',
      subtitle: lost > 0
        ? `−${lost.toLocaleString()} biomass${this.deathStreak > 1 ? ' · shrank a size' : ' · back to your last evolution'} · parts kept`
        : 'Reforming · parts kept',
      durationSec: 3,
    });
    bus.emit('fx:flash', { color: 0xff5050, alpha: 0.4, duration: 0.6 });
    bus.emit('fx:screenShake', { intensity: 14, duration: 0.45 });
    bus.emit('fx:timeSlow', { factor: 0.35, duration: 0.8 });
    bus.emit('audio:cue', { id: 'event', volume: 0.6 });
    this.saveLive();
  }

  /** Snapshot the live run into the save so Continue / reload resumes it. */
  private saveLive(): void {
    const run = saveManager.getRun();
    const live: CambrianLive = {
      parts: { ...this.parts },
      evolutionsEarned: this.evolutionsEarned,
      deathStreak: this.deathStreak,
      hp: this.playerHp,
      atp: this.playerAtp,
      apexSpawned: this.apexEntity !== 0,
      completed: this.goalReached,
      killsByTier: { ...this.killsByTier },
      deaths: this.deaths,
    };
    run.stageData.cambrian = { ...(run.stageData.cambrian ?? {}), live };
  }
  private saveLiveAcc = 0;

  private tickProjectiles(dt: number): void {
    const w = this.ctx.world;
    // Build a quick {entity -> creature pos} list for collision. Quills hit
    // anything but the special medusae / siphonophores.
    const creatures: Array<{ id: number; x: number; y: number; r: number }> = [];
    for (const e of w.query([CreatureComp, Position])) {
      const c = w.get(e, CreatureComp)!;
      if (c.defId === 'glow_medusa' || c.defId === 'toxic_siphon') continue;
      const p = w.get(e, Position)!;
      creatures.push({ id: e, x: p.x, y: p.y, r: c.size });
    }
    const expired: number[] = [];
    for (const e of w.query([ProjectileComp, Position])) {
      const proj = w.get(e, ProjectileComp)!;
      const p = w.get(e, Position)!;
      proj.age += dt;
      if (proj.age >= proj.lifetime) { expired.push(e); continue; }
      p.x += proj.vx * dt;
      p.y += proj.vy * dt;
      // Hit-test creatures (skip already-hit on pierce).
      for (const c of creatures) {
        if (proj.hitSet.has(c.id)) continue;
        const dx = c.x - p.x;
        const dy = c.y - p.y;
        const rr = (c.r + proj.size);
        if (dx * dx + dy * dy < rr * rr) {
          proj.hitSet.add(c.id);
          this.damageCreature(c.id, proj.damage);
          bus.emit('fx:particles', { x: p.x, y: p.y, count: 5, color: 0xd0a0ff, spread: 30 });
          proj.pierce -= 1;
          if (proj.pierce <= 0) { expired.push(e); break; }
        }
      }
    }
    // Sync projectile graphics positions; reap expired.
    const expiredSet = new Set(expired);
    for (const child of [...this.projectileLayer.children]) {
      const id = (child as any).__entity as number | undefined;
      if (!id) continue;
      if (expiredSet.has(id)) {
        this.projectileLayer.removeChild(child);
        this.reap.push(child);
        if (w.exists(id)) w.destroyEntity(id);
        continue;
      }
      const p = w.get(id, Position);
      if (p) child.position.set(p.x, p.y);
    }
  }

  /** `bite` = eaten by a lunge (special prey effects only apply then). */
  private damageCreature(entityId: number, dmg: number, bite = false): void {
    const w = this.ctx.world;
    const c = w.get(entityId, CreatureComp);
    const p = w.get(entityId, Position);
    if (!c || !p) return;
    c.hp -= dmg;
    if (c.hp <= 0) {
      // Find def for biomass + xp payout.
      const def = this.creatures.find((d) => d.id === c.defId);
      const biomass = (def?.biomass ?? 0) * (c.scale ?? 1);
      const xp = def?.xp ?? 0;
      if (def?.effect && bite) this.applyEatEffect(def.effect, p.x, p.y);
      bus.emit('cambrian:kill', {
        killerId: this.playerEntity,
        victimId: entityId,
        victimTier: c.tier,
        victimDefId: c.defId,
        byPlayer: true,
        x: p.x,
        y: p.y,
        biomass,
        xp,
      });
    }
  }

  /** Special prey: medusae patch you up, siphonophores sting on the way down. */
  private applyEatEffect(effect: 'heal' | 'poison', x: number, y: number): void {
    if (effect === 'heal') {
      const heal = Math.round(this.playerHpMax * HEAL_FRAC);
      this.playerHp = Math.min(this.playerHpMax, this.playerHp + heal);
      bus.emit('fx:floatingText', { x, y: y - 30, text: `+${heal} HP`, color: 0x80ff90 });
      bus.emit('fx:particles', { x: this.px, y: this.py, count: 14, color: 0x80ff90, spread: 60 });
      bus.emit('audio:cue', { id: 'milestone', volume: 0.3 });
    } else {
      bus.emit('fx:floatingText', { x, y: y - 30, text: 'POISON!', color: 0xd070ff });
      bus.emit('fx:particles', { x: this.px, y: this.py, count: 16, color: 0xd070ff, spread: 70 });
      this.lungeT = 0;
      this.applyPlayerDamage(this.playerHpMax * POISON_FRAC, x, y, 0, false);
    }
  }

  private updateCamera(): void {
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;
    const z = this.worldZoom;
    let cx = sw / 2 - this.camX * z;
    let cy = sh / 2 - this.camY * z;
    if (sw < this.worldW * z) {
      cx = Math.max(sw - this.worldW * z, Math.min(0, cx));
    }
    if (sh < this.worldH * z) {
      cy = Math.max(sh - this.worldH * z, Math.min(0, cy));
    }
    this.worldContainer.scale.set(z);
    this.worldContainer.position.set(cx, cy);
  }

  /** Pinch-zoom (mobile): the player overrides the fitted zoom. */
  zoomBy(factor: number): void {
    this.worldZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, this.worldZoom * factor));
    this.updateCamera();
  }

  // ------------------------------------------------------------------
  // DOM HUD — biomass / size / timer, evolve goal, HP + ATP, frenzy.
  // Bare minimum for M1; M4 adds the body sidebar; M2 adds ecosystem bars.
  // ------------------------------------------------------------------
  private buildDomHud(): void {
    this.buildEcoPanel();
    this.buildGoalBanner();
    const hud = document.createElement('div');
    hud.id = 'cambrian-hud';
    hud.innerHTML = `
      <div class="ch-row">
        <div class="ch-cell"><div class="ch-label">BIOMASS</div><div class="ch-val" data-r="biomass">0</div></div>
        <div class="ch-cell"><div class="ch-label">SIZE</div><div class="ch-val" data-r="size">1</div></div>
        <div class="ch-cell"><div class="ch-label">TIME</div><div class="ch-val" data-r="timer">00:00</div></div>
      </div>
      <div class="ch-goal">
        <div class="ch-goal-text" data-r="goalText"></div>
        <div class="ch-goal-track"><div class="ch-goal-fill" data-r="goalBar"></div></div>
        <div class="ch-goal-next" data-r="goalNext"></div>
      </div>
      <div class="ch-bars">
        <div class="ch-bar-cell">
          <div class="ch-bar-label ch-hp-label">HP</div>
          <div class="ch-bar-track"><div class="ch-bar-fill ch-bar-hp" data-r="hpBar"></div></div>
          <div class="ch-bar-num" data-r="hp">100/100</div>
        </div>
        <div class="ch-bar-cell" data-r="atpCell">
          <div class="ch-bar-label ch-atp-label">ATP</div>
          <div class="ch-bar-track ch-atp-track"><div class="ch-bar-fill ch-bar-atp" data-r="atpBar"></div><div class="ch-atp-notches" data-r="atpNotches"></div></div>
          <div class="ch-bar-num" data-r="atp">100/100</div>
        </div>
        <div class="ch-lunge-hint">BITE · click / space −45 ATP &nbsp; DODGE · right-click (×2 = left) −50 ATP</div>
      </div>
      <div class="ch-frenzy" data-r="frenzy">
        <span class="cf-x">FRENZY ×<b data-r="frenzyN">0</b></span>
        <span class="cf-mult" data-r="frenzyMult"></span>
        <div class="cf-track"><div class="cf-fill" data-r="frenzyBar"></div></div>
      </div>
      <div class="ch-archetype" data-r="archetype">Cambrian Survivors</div>
    `;
    document.body.appendChild(hud);
    this.domHud = hud;

    const ver = document.createElement('div');
    ver.id = 'cambrian-version';
    ver.textContent = VERSION;
    document.body.appendChild(ver);
    this.domVersion = ver;

    this.injectHudStyle();
    this.hudRefs.biomass = hud.querySelector('[data-r="biomass"]') as HTMLElement;
    this.hudRefs.size = hud.querySelector('[data-r="size"]') as HTMLElement;
    this.hudRefs.timer = hud.querySelector('[data-r="timer"]') as HTMLElement;
    this.hudRefs.hp = hud.querySelector('[data-r="hp"]') as HTMLElement;
    this.hudRefs.hpBar = hud.querySelector('[data-r="hpBar"]') as HTMLElement;
    this.hudRefs.goalText = hud.querySelector('[data-r="goalText"]') as HTMLElement;
    this.hudRefs.goalBar = hud.querySelector('[data-r="goalBar"]') as HTMLElement;
    this.hudRefs.goalNext = hud.querySelector('[data-r="goalNext"]') as HTMLElement;
    this.hudRefs.archetype = hud.querySelector('[data-r="archetype"]') as HTMLElement;
    this.hudRefs.atpBar = hud.querySelector('[data-r="atpBar"]') as HTMLElement;
    this.hudRefs.atpCell = hud.querySelector('[data-r="atpCell"]') as HTMLElement;
    this.hudRefs.atpNotches = hud.querySelector('[data-r="atpNotches"]') as HTMLElement;
    this.hudRefs.atp = hud.querySelector('[data-r="atp"]') as HTMLElement;
    this.hudRefs.frenzy = hud.querySelector('[data-r="frenzy"]') as HTMLElement;
    this.hudRefs.frenzyN = hud.querySelector('[data-r="frenzyN"]') as HTMLElement;
    this.hudRefs.frenzyMult = hud.querySelector('[data-r="frenzyMult"]') as HTMLElement;
    this.hudRefs.frenzyBar = hud.querySelector('[data-r="frenzyBar"]') as HTMLElement;

  }

  /** Goal banner — large transient title (wave incoming, apex spawn, etc).
   *  Listens to stage:goalBanner from the EventBus; subscriber lives on the
   *  domRemovers list so it tears down with the stage. */
  private buildGoalBanner(): void {
    const banner = document.createElement('div');
    banner.id = 'cambrian-goal-banner';
    banner.innerHTML = '<div class="cgb-title"></div><div class="cgb-sub"></div>';
    document.body.appendChild(banner);
    this.domGoalBanner = banner;
    this.domRemovers.push(bus.on('stage:goalBanner', (p) => {
      if (!this.domGoalBanner) return;
      const t = this.domGoalBanner.querySelector('.cgb-title') as HTMLElement;
      const s = this.domGoalBanner.querySelector('.cgb-sub') as HTMLElement;
      t.textContent = p.title;
      s.textContent = p.subtitle;
      this.domGoalBanner.classList.add('visible');
      this.domGoalBannerHideAt = this.elapsed + p.durationSec;
    }));
  }

  private updateGoalBanner(): void {
    if (!this.domGoalBanner) return;
    if (this.domGoalBannerHideAt > 0 && this.elapsed >= this.domGoalBannerHideAt) {
      this.domGoalBanner.classList.remove('visible');
      this.domGoalBannerHideAt = 0;
    }
  }

  /** Ecosystem HUD panel — 4 horizontal bars (one per tier) bottom-left.
   *  Color-coded: green = healthy (>60%), yellow = stressed (30-60%), red = collapsing (<30%). */
  private buildEcoPanel(): void {
    const panel = document.createElement('div');
    panel.id = 'cambrian-eco-panel';
    const tiers: Array<{ tier: number; label: string }> = [
      { tier: 1, label: 'PLANKTON' },
      { tier: 2, label: 'PREY' },
      { tier: 3, label: 'HUNTERS' },
      { tier: 4, label: 'APEX' },
    ];
    panel.innerHTML = `
      <div class="ce-title">ECOSYSTEM</div>
      ${tiers.map((t) => `
        <div class="ce-row" data-tier="${t.tier}">
          <div class="ce-tag">T${t.tier}</div>
          <div class="ce-name">${t.label}</div>
          <div class="ce-track"><div class="ce-fill"></div></div>
          <div class="ce-num">0/0</div>
        </div>
      `).join('')}
    `;
    document.body.appendChild(panel);
    this.domEcoPanel = panel;
    this.hudRefs.ecoBars = [];
    this.hudRefs.ecoNums = [];
    for (const t of tiers) {
      const row = panel.querySelector(`[data-tier="${t.tier}"]`) as HTMLElement;
      this.hudRefs.ecoBars.push(row.querySelector('.ce-fill') as HTMLElement);
      this.hudRefs.ecoNums.push(row.querySelector('.ce-num') as HTMLElement);
    }
  }

  private updateEcoBars(p: { populations: Record<number, number>; caps: Record<number, number> }): void {
    if (!this.hudRefs.ecoBars) return;
    for (let i = 0; i < 4; i++) {
      const tier = i + 1;
      const cur = p.populations[tier] ?? 0;
      const cap = p.caps[tier] ?? 1;
      const frac = Math.max(0, Math.min(1, cur / Math.max(1, cap)));
      const bar = this.hudRefs.ecoBars[i];
      const num = this.hudRefs.ecoNums![i];
      bar.style.width = `${frac * 100}%`;
      bar.classList.remove('ce-healthy', 'ce-stressed', 'ce-collapsing');
      if (frac > 0.6) bar.classList.add('ce-healthy');
      else if (frac > 0.3) bar.classList.add('ce-stressed');
      else bar.classList.add('ce-collapsing');
      num.textContent = `${cur}/${cap}`;
    }
  }

  private updateHud(): void {
    if (!this.hudRefs.biomass) return;
    this.hudRefs.biomass.textContent = Math.floor(player.biomass).toLocaleString();
    this.hudRefs.size!.textContent = `${this.growthIdx + 1} · eats T${this.maxBiteTier}`;
    const mm = Math.floor(this.elapsed / 60).toString().padStart(2, '0');
    const ss = Math.floor(this.elapsed % 60).toString().padStart(2, '0');
    this.hudRefs.timer!.textContent = `${mm}:${ss}`;
    const hpFrac = this.playerHp / this.playerHpMax;
    this.hudRefs.hp!.textContent = `${Math.max(0, Math.floor(this.playerHp))}/${this.playerHpMax}`;
    this.hudRefs.hpBar!.style.width = `${Math.max(0, hpFrac) * 100}%`;
    // Goal bar + next growth — same readout L2 puts at the top.
    if (this.hudRefs.goalText) {
      const b = player.biomass;
      this.hudRefs.goalText.textContent = `Biomass  ${Math.floor(b).toLocaleString()} / ${STAGE_GOAL_BIOMASS.toLocaleString()}`;
      this.hudRefs.goalBar!.style.width = `${Math.min(1, b / STAGE_GOAL_BIOMASS) * 100}%`;
      if (this.growthIdx < GROWTH_THRESHOLDS.length) {
        const need = GROWTH_THRESHOLDS[this.growthIdx] - b;
        const flipAt = maxBiteTierFor(this.growthIdx + 1) > this.maxBiteTier
          ? ` · tier ${maxBiteTierFor(this.growthIdx + 1)} becomes prey` : ' biomass';
        this.hudRefs.goalNext!.textContent = `EVOLVE IN ${Math.max(0, Math.ceil(need)).toLocaleString()}${flipAt}`;
      } else {
        this.hudRefs.goalNext!.textContent = this.goalReached ? 'THRESHOLD REACHED' : 'FULLY EVOLVED';
      }
    }
    // ATP + lunge readiness: the cell lights up when a lunge is available.
    if (this.hudRefs.atpBar && this.hudRefs.atpCell) {
      this.hudRefs.atpBar.style.width = `${(this.playerAtp / this.atpMax) * 100}%`;
      this.hudRefs.atp!.textContent = `${Math.floor(this.playerAtp)}/${Math.round(this.atpMax)}`;
      // Notches mark each dodge's worth of ATP.
      const notches = this.hudRefs.atpNotches!;
      const n = Math.round(this.atpMax / FLICK_ATP_COST);
      if (notches.dataset.n !== String(n)) {
        notches.dataset.n = String(n);
        notches.style.backgroundImage = `repeating-linear-gradient(90deg, transparent 0 calc(${100 / n}% - 2px), rgba(0,0,0,0.8) calc(${100 / n}% - 2px) ${100 / n}%)`;
      }
      const ready = this.playerAtp >= FLICK_ATP_COST;
      if (this.touchPad) {
        this.touchPad.setReady('bite', this.playerAtp >= LUNGE_ATP_COST && this.lungeCooldownT <= 0);
        this.touchPad.setReady('dodgeL', ready && this.flickCooldownT <= 0);
        this.touchPad.setReady('dodgeR', ready && this.flickCooldownT <= 0);
      }
      this.hudRefs.atpCell.classList.toggle('ready', ready);
    }
    // Frenzy readout — only shown while a chain is alive.
    if (this.hudRefs.frenzy) {
      const on = this.frenzy > 0 && this.frenzyT > 0;
      this.hudRefs.frenzy.classList.toggle('on', on);
      if (on) {
        this.hudRefs.frenzyN!.textContent = String(this.frenzy);
        this.hudRefs.frenzyMult!.textContent = `${this.frenzyBiomassMult().toFixed(1)}× biomass`;
        this.hudRefs.frenzyBar!.style.width = `${(this.frenzyT / FRENZY_WINDOW) * 100}%`;
      }
    }
  }

  private injectHudStyle(): void {
    if (document.getElementById('cambrian-hud-styles')) return;
    const style = document.createElement('style');
    style.id = 'cambrian-hud-styles';
    style.textContent = `
#cambrian-hud {
  position: fixed;
  top: 14px; left: 50%;
  transform: translateX(-50%);
  display: flex; flex-direction: column; gap: 8px;
  z-index: 50;
  font-family: 'Inter', system-ui, sans-serif;
  color: #fff5dc;
  pointer-events: none;
  user-select: none;
}
#cambrian-hud .ch-row { display: flex; gap: 12px; }
#cambrian-hud .ch-cell {
  padding: 8px 16px;
  background: rgba(26, 12, 4, 0.6);
  border: 1px solid rgba(255, 200, 120, 0.18);
  border-radius: 10px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  min-width: 64px; text-align: center;
}
#cambrian-hud .ch-label { font-size: 9px; letter-spacing: 0.22em; opacity: 0.6; }
#cambrian-hud .ch-val { font-size: 16px; font-weight: 700; }
#cambrian-hud .ch-bars { display: flex; flex-direction: column; align-items: center; gap: 5px; }
#cambrian-hud .ch-bar-cell { display: flex; align-items: center; gap: 8px; }
#cambrian-hud .ch-bar-label { font-size: 11px; font-weight: 800; letter-spacing: 0.14em; width: 30px; text-align: right; }
#cambrian-hud .ch-hp-label { color: #ff8a70; }
#cambrian-hud .ch-atp-label { color: #7cc8ff; }
#cambrian-hud .ch-bar-track {
  width: min(240px, calc(100vw - 150px)); height: 12px; background: rgba(0,0,0,0.5);
  border: 1px solid rgba(255,200,120,0.2); border-radius: 6px; overflow: hidden;
}
#cambrian-hud .ch-atp-track { border-color: rgba(120, 200, 255, 0.35); }
#cambrian-hud .ch-bar-fill { height: 100%; transition: width 0.15s ease; }
#cambrian-hud .ch-atp-track { position: relative; }
#cambrian-hud .ch-atp-notches { position: absolute; inset: 0; pointer-events: none; }
#cambrian-hud .ch-bar-hp { background: linear-gradient(90deg, #ff5050, #ffa050); }
#cambrian-hud .ch-bar-xp { background: linear-gradient(90deg, #ffe9aa, #ffbe55); }
#cambrian-hud .ch-bar-num { font-size: 11px; font-weight: 600; opacity: 0.85; min-width: 56px; text-align: left; font-variant-numeric: tabular-nums; }
#cambrian-hud .ch-goal { display: flex; flex-direction: column; align-items: center; gap: 4px; }
#cambrian-hud .ch-goal-text { font-size: 12px; font-weight: 600; letter-spacing: 1px; color: #fff5dc; text-shadow: 0 0 4px rgba(0,0,0,0.6); font-variant-numeric: tabular-nums; }
#cambrian-hud .ch-goal-track { width: min(360px, calc(100vw - 80px)); height: 6px; background: rgba(0,0,0,0.45); border: 1px solid rgba(255,200,120,0.45); box-sizing: border-box; }
#cambrian-hud .ch-goal-fill { height: 100%; width: 0; background: rgba(255, 200, 112, 0.9); transition: width 0.15s ease; }
#cambrian-hud .ch-goal-next { font-size: 20px; font-weight: 800; letter-spacing: 1px; color: #ffd070; margin-top: 4px; text-shadow: 0 0 6px rgba(0,0,0,0.8), 0 0 12px rgba(255,180,60,0.35); font-variant-numeric: tabular-nums; }
#cambrian-hud .ch-bar-atp { background: linear-gradient(90deg, #4fb8ff, #9ad8ff); opacity: 0.6; transition: width 0.1s linear, opacity 0.15s; }
#cambrian-hud .ch-lunge-hint { font-size: 9px; letter-spacing: 0.14em; opacity: 0.45; }
#cambrian-hud .ch-bar-cell.ready .ch-bar-atp { opacity: 1; box-shadow: 0 0 10px rgba(120, 200, 255, 0.7); }
/* Phones: tighter HUD, and the ecosystem panel gives way to the pad. */
@media (max-width: 760px), (max-height: 500px) {
  #cambrian-hud { top: 8px; gap: 4px; }
  #cambrian-hud .ch-cell { min-width: 52px; padding: 5px 8px; }
  #cambrian-hud .ch-val { font-size: 13px; }
  #cambrian-hud .ch-label { font-size: 8px; letter-spacing: 0.16em; }
  #cambrian-hud .ch-goal-next { font-size: 15px; }
  #cambrian-hud .ch-goal-track { width: min(280px, calc(100vw - 40px)); }
  #cambrian-hud .ch-bar-track { width: min(180px, calc(100vw - 170px)); }
  #cambrian-hud .ch-lunge-hint { display: none; }
  #cambrian-codex-btn { top: auto; bottom: calc(112px + env(safe-area-inset-bottom)); right: 10px; padding: 6px 9px; }
}
/* Landscape phone: HP + ATP leave the centre column for a compact pair
   in the top-left corner (free in this stage), and the EVOLVE IN counter
   drops to a small line. #cambrian-hud is centred without a transform
   here — a transformed ancestor would trap the fixed-position bars. */
@media (max-height: 500px) {
  #cambrian-hud { left: 0; right: 0; margin: 0 auto; width: max-content; transform: none; }
  #cambrian-hud .ch-goal-next { font-size: 11px; margin-top: 1px; letter-spacing: 0.5px; }
  #cambrian-hud .ch-bars {
    position: fixed;
    top: max(8px, env(safe-area-inset-top));
    left: max(10px, env(safe-area-inset-left));
    align-items: flex-start;
    gap: 3px;
    padding: 5px 8px;
    border-radius: 9px;
    background: rgba(26, 12, 4, 0.55);
    border: 1px solid rgba(255, 200, 120, 0.15);
  }
  #cambrian-hud .ch-bar-cell { gap: 5px; }
  #cambrian-hud .ch-bar-label { font-size: 8px; width: 20px; letter-spacing: 0.08em; }
  #cambrian-hud .ch-bar-track { width: 96px; height: 6px; border-radius: 3px; }
  #cambrian-hud .ch-bar-num { font-size: 8px; min-width: 38px; }
}
body.has-pad #cambrian-eco-panel { display: none; }
body.has-pad #cambrian-hint { bottom: calc(124px + env(safe-area-inset-bottom)); }
body.has-pad #cambrian-version { opacity: 0.25; }
#cambrian-hud .ch-frenzy {
  display: flex; align-items: center; gap: 10px; justify-content: center;
  height: 0; opacity: 0; overflow: hidden; transition: opacity 0.2s, height 0.2s;
  font-size: 11px; letter-spacing: 0.22em; color: #ffd070;
  text-shadow: 0 0 10px rgba(255, 200, 80, 0.6);
}
#cambrian-hud .ch-frenzy.on { height: 16px; opacity: 1; }
#cambrian-hud .ch-frenzy b { font-size: 14px; }
#cambrian-hud .cf-mult { font-size: 10px; letter-spacing: 0.12em; opacity: 0.8; color: #fff5dc; }
#cambrian-hud .cf-track { width: 120px; height: 4px; background: rgba(0,0,0,0.5); border-radius: 2px; overflow: hidden; }
#cambrian-hud .cf-fill { height: 100%; background: linear-gradient(90deg, #ffe9aa, #ffbe55); transition: width 0.08s linear; }
#cambrian-hud .ch-archetype {
  text-align: center; font-size: 11px; letter-spacing: 0.3em; opacity: 0.55;
  text-transform: uppercase;
}
#cambrian-hud .ch-archetype.declared { color: #ffd070; opacity: 1; text-shadow: 0 0 12px rgba(255, 200, 100, 0.5); }
#cambrian-version {
  position: fixed; right: 12px; bottom: 12px;
  font-family: 'Inter', system-ui, sans-serif; font-size: 10px; letter-spacing: 0.18em;
  color: rgba(255, 220, 160, 0.4); pointer-events: none; user-select: none;
  z-index: 50;
}
#cambrian-eco-panel {
  position: fixed;
  left: 14px; bottom: 14px;
  padding: 10px 12px;
  background: rgba(20, 8, 4, 0.7);
  border: 1px solid rgba(255, 200, 120, 0.18);
  border-radius: 10px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  font-family: 'Inter', system-ui, sans-serif;
  color: #fff5dc;
  z-index: 50;
  pointer-events: none;
  user-select: none;
  min-width: 240px;
}
#cambrian-eco-panel .ce-title { font-size: 9px; letter-spacing: 0.3em; opacity: 0.55; margin-bottom: 6px; }
#cambrian-eco-panel .ce-row { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
#cambrian-eco-panel .ce-tag { font-size: 9px; opacity: 0.55; width: 18px; }
#cambrian-eco-panel .ce-name { font-size: 9px; letter-spacing: 0.14em; width: 60px; opacity: 0.75; }
#cambrian-eco-panel .ce-track { flex: 1; height: 8px; background: rgba(0,0,0,0.5); border-radius: 4px; overflow: hidden; }
#cambrian-eco-panel .ce-fill { height: 100%; transition: width 0.2s ease; background: #4FD1C5; }
#cambrian-eco-panel .ce-fill.ce-healthy   { background: linear-gradient(90deg, #50ff8a, #20cc60); }
#cambrian-eco-panel .ce-fill.ce-stressed  { background: linear-gradient(90deg, #ffd060, #ffa030); }
#cambrian-eco-panel .ce-fill.ce-collapsing{ background: linear-gradient(90deg, #ff5050, #c02020); }
#cambrian-eco-panel .ce-num { font-size: 9px; opacity: 0.75; min-width: 42px; text-align: right; }

  position: fixed; right: 14px; bottom: 38px;
  padding: 10px 14px; border-radius: 10px;
  background: rgba(20, 8, 4, 0.7);
  border: 1px solid rgba(255, 200, 120, 0.18);
  backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
  font-family: 'Inter', system-ui, sans-serif; color: #fff5dc;
  z-index: 50; pointer-events: none; user-select: none;
  min-width: 230px;
}

#cambrian-apex-arrow {
  position: fixed; transform: translate(-50%, -50%); pointer-events: none; z-index: 60;
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  font-family: 'Inter', system-ui, sans-serif; color: #ff5550;
}
#cambrian-apex-arrow .ca-tri {
  width: 0; height: 0;
  border-left: 16px solid transparent;
  border-right: 16px solid transparent;
  border-bottom: 22px solid #ff5550;
  filter: drop-shadow(0 0 8px rgba(255, 50, 30, 0.6));
}
#cambrian-apex-arrow .ca-lbl { font-size: 9px; letter-spacing: 0.3em; font-weight: 700; opacity: 0.95; text-shadow: 0 0 8px rgba(255, 50, 30, 0.6); }

#cambrian-goal-banner {
  position: fixed; left: 50%; top: 30%;
  transform: translate(-50%, -50%);
  pointer-events: none; user-select: none; z-index: 100;
  font-family: 'Inter', system-ui, sans-serif; text-align: center;
  opacity: 0; transition: opacity 0.35s ease;
}
#cambrian-goal-banner.visible { opacity: 1; }
#cambrian-goal-banner .cgb-title {
  font-size: 32px; font-weight: 800; letter-spacing: 0.3em;
  color: #ffd070;
  text-shadow:
    0 0 20px rgba(255, 200, 80, 0.85),
    0 0 40px rgba(255, 100, 30, 0.6);
  animation: cgbBreath 1.4s ease-in-out infinite;
}
#cambrian-goal-banner .cgb-sub {
  font-size: 13px; letter-spacing: 0.18em; opacity: 0.85;
  color: #fff5dc; margin-top: 6px;
  text-shadow: 0 0 12px rgba(0, 0, 0, 0.65);
}
@keyframes cgbBreath { 0%,100% { transform: scale(1); } 50% { transform: scale(1.04); } }
@media (max-height: 500px) {
  #cambrian-goal-banner { top: 26%; }
  #cambrian-goal-banner .cgb-title { font-size: 17px; letter-spacing: 0.2em; }
  #cambrian-goal-banner .cgb-sub { font-size: 9px; margin-top: 3px; }
}
`;
    document.head.appendChild(style);
  }

  cleanup(): void {
    if (this.onPointerMove) {
      this.ctx.app.canvas.removeEventListener('pointermove', this.onPointerMove);
    }
    if (this.onPointerDown) this.ctx.app.canvas.removeEventListener('pointerdown', this.onPointerDown);
    if (this.onContextMenu) this.ctx.app.canvas.removeEventListener('contextmenu', this.onContextMenu);
    if (this.onKeyDown) window.removeEventListener('keydown', this.onKeyDown);
    if (this.intro) { this.intro.destroy(); this.intro = null; }
    this.flushReap();
    for (const off of this.domRemovers) off();
    this.domRemovers.length = 0;
    if (this.domHud) { this.domHud.remove(); this.domHud = null; }
    if (this.domVersion) { this.domVersion.remove(); this.domVersion = null; }
    if (this.domEcoPanel) { this.domEcoPanel.remove(); this.domEcoPanel = null; }
    if (this.domGoalBanner) { this.domGoalBanner.remove(); this.domGoalBanner = null; }
    if (this.domApexArrow) { this.domApexArrow.remove(); this.domApexArrow = null; }
    document.body.classList.remove('stage-cursor');
    this.levelUpModal?.close();
    this.bodyPlanOverlay?.close();
    this.levelComplete.close();
    this.tutorial?.destroy();
    this.tutorial = null;
    this.spotlight?.destroy();
    this.spotlight = null;
    this.codexBook?.destroy();
    this.codexBook = null;
    this.achievements?.destroy();
    this.achievements = null;
    this.touchPad?.destroy();
    this.touchPad = null;
    this.saveLive();
    // Destroy all spawned entities tied to this stage.
    const w = this.ctx.world;
    for (const e of [...w.query([CreatureComp])]) w.destroyEntity(e);
    for (const e of [...w.query([ProjectileComp])]) w.destroyEntity(e);
    if (w.exists(this.playerEntity)) w.destroyEntity(this.playerEntity);
    this.layer.destroy({ children: true });
    // Persist current run state on stage exit.
    saveManager.save();
  }

  onResize(_w: number, _h: number): void {
    this.updateCamera();
  }
}
